import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import Modal from 'react-modal';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { getFirebase, getCurrentUserEnv } from '@services/firebase';
import { FaSync, FaPlus, FaPrint, FaSave, FaSort, FaSortUp, FaSortDown, FaTimes, FaTrash } from 'react-icons/fa';
import { TbAutomation } from 'react-icons/tb';
import { expandArrayColumns } from '../utils/dataUtils';
import { establishDbConnection, fetchFirestoreCollections, readFirestoreCollection, fetchCollectionDocIds, fetchDocumentById} from '../utils/firestoreUtils';
import { getAuth } from 'firebase/auth';
import { listObjectsInFolder, readObjectContent, uploadToStorage, deleteFromStorage } from '../utils/storageUtils';
import { openImagePrintPreview } from '../components/imagePrintPreview.jsx';
import { formatFileSize } from '../utils/format';
import { createSaveModalConfig } from '../components/modals/SaveModal';
import DynamicFormModal from '../components/modals/DynamicFormModal';
import MediaPreviewModal from '../components/modals/MediaPreviewModal';
import DeleteModal from '../components/modals/DeleteModal';
import { useGoogleDrive } from '../contexts/GoogleDriveContext'; 
import './css/HouseKeeping.css';

Modal.setAppElement('#root');

const getFirestoreFunctionsBase = () =>
  getCurrentUserEnv() === 'Test'
    ? 'https://us-central1-pcakedatastoretest.cloudfunctions.net'
    : 'https://us-central1-pcakedatastore.cloudfunctions.net';

const getDeleteDocEndpoint = () =>
  `${getFirestoreFunctionsBase()}/${getCurrentUserEnv() === 'Test' ? 'deleteDocumentTest' : 'deleteDocument'}`;

const getDeleteEmptyColEndpoint = () =>
  `${getFirestoreFunctionsBase()}/${getCurrentUserEnv() === 'Test' ? 'deleteEmptyCollectionTest' : 'deleteEmptyCollection'}`;

const HouseKeeping = ({ userRole }) => {
  const navigate = useNavigate();
  const showToast = useToast();
  const blobUrlRef = useRef(new Set());
  const fileInputRef = useRef(null);

  const handleClose = () => navigate('/profile');
  const {
    isDriveConnected,
    googleSheets,
    googleForms,
    isLoading: driveLoading,
    error: driveError,
    hasDriveAccess,
    connectToGoogleDrive,
    fetchSheetData,
    fetchSheetMetadata,
    fetchGoogleSheets,
    fetchFormResponses,
    fetchFormDetails,
    fetchGoogleForms,
    deleteGoogleSheet,
    deleteFormResponse,
    deleteSheetRows,
    disconnectGoogleDrive,
    clearTokensAndReconnect
  } = useGoogleDrive();
  const connectMenuRef = useRef(null);
  const [isConnectMenuOpen, setIsConnectMenuOpen] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [filteredFileData, setFilteredFileData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [fileFormat, setFileFormat] = useState('csv');
  const [showConnectedMessage, setShowConnectedMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [otemplateDocs, setOtemplateDocs] = useState([]);
  const [selectedOtemplateDoc, setSelectedOtemplateDoc] = useState(null);
  const [firestoreCollections, setFirestoreCollections] = useState([]);
  const [firestoreCollection, setFirestoreCollection] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isAutomationMode, setIsAutomationMode] = useState(false);
  const [sheetTabs, setSheetTabs] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [GoogleSheets, setGoogleSheets] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [fileDataToSave, setFileDataToSave] = useState([]);
  const [initialFolders, setInitialFolders] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [storeFiles, setStoreFiles] = useState([]);
  const [selectedStoreFile, setSelectedStoreFile] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [availableBuckets, setAvailableBuckets] = useState([]);
  const defaultBucket = getCurrentUserEnv() === 'Test'
    ? (import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || 'pcakedatastoretest.firebasestorage.app')
    : 'pcakeds_csv_backup1';
  const [selectedBucket, setSelectedBucket] = useState(defaultBucket);
  const [fileType, setFileType] = useState('');
  const [isMediaFile, setIsMediaFile] = useState(false);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [folderHierarchy, setFolderHierarchy] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activeModalConfig, setActiveModalConfig] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mediaPreviewOpen, setMediaPreviewOpen] = useState(false);
  const [deleteItems, setDeleteItems] = useState([]);
  const [deleteContext, setDeleteContext] = useState(null);
  const [driveErrorMessage, setDriveErrorMessage] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formResponseData, setFormResponseData] = useState([]);
  const [editingHeaderIndex, setEditingHeaderIndex] = useState(null);
  const [editingHeaderValue, setEditingHeaderValue] = useState('');
  const [originalHeaders, setOriginalHeaders] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [selectAllColumns, setSelectAllColumns] = useState(true);
  const [showReconnectButton, setShowReconnectButton] = useState(false);

  const handleDriveReconnect = async () => {
    try {
      // Clear existing tokens
      localStorage.removeItem('googleDriveAccessToken');
      localStorage.removeItem('googleDriveRefreshToken');
      localStorage.removeItem('googleDriveTokenExpiry');
      
      // Disconnect from context
      disconnectGoogleDrive();
      
      // Show message
      setErrorMessage('Reconnecting with new permissions...');
      setShowReconnectButton(false);
      
      // Reconnect
      await connectToGoogleDrive();
    } catch (error) {
      setErrorMessage(`Failed to reconnect: ${error.message}`);
    }
  };

  const shouldShowDeleteButton = () => {
    if (!activeConnection) return false;
    
    switch (activeConnection) {
      case 'db':
        return !!selectedTable;
      case 'store':
        return !!selectedStoreFile || selectedRecords.length > 0;
      case 'drive':
        return !!selectedSheet || !!selectedForm;
      default:
        return false;
    }
  };

  const getDeleteButtonTitle = () => {
    if (activeConnection === 'drive') {
      if (selectedRecords.length > 0) {
        const itemType = selectedSheet ? 'rows' : 'responses';
        return `Delete ${selectedRecords.length} selected ${itemType}`;
      }
      const itemType = selectedSheet ? 'sheet' : 'form';
      return `Delete entire ${itemType}`;
    }
    
    if (activeConnection === 'store') {
      if (selectedRecords.length > 1) {
        return `Delete ${selectedRecords.length} files`;
      }
      if (selectedRecords.length === 1) {
        return "Delete selected file";
      }
      if (selectedStoreFile) {
        return "Delete selected file";
      }
    }
    
    return "Delete";
  };

  const buildFolderHierarchy = (prefixes) => {
    const hierarchy = [];
   
    prefixes.forEach(prefix => {
      const path = prefix.name.replace(/\/$/, '');
      const parts = path.split('/').filter(part => part);
     
      let currentLevel = hierarchy;
      parts.forEach((part, index) => {
        let existingFolder = currentLevel.find(folder => folder.name === part);
       
        if (!existingFolder) {
          existingFolder = {
            name: part,
            fullPath: parts.slice(0, index + 1).join('/'),
            children: []
          };
          currentLevel.push(existingFolder);
        }
       
        currentLevel = existingFolder.children;
      });
    });
   
    return hierarchy;
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (connectMenuRef.current && !connectMenuRef.current.contains(event.target)) {
        setIsConnectMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const initializeStoreConnection = useCallback(async (bucketName = '') => {
    try {
      const targetBucket = bucketName || selectedBucket || defaultBucket;
      const { items, prefixes } = await listObjectsInFolder('', targetBucket);
      const folderStructure = buildFolderHierarchy(prefixes);
      setFolderHierarchy(folderStructure);
      setCurrentPath('');
      const folderPrefixes = prefixes.map(p => p.name.replace(/\/$/, ''));
      if (initialFolders.length === 0) {
        setInitialFolders([...new Set(folderPrefixes)]);
      }
      setFolders([...new Set(folderPrefixes)]);
      setSelectedFolder('');
      setStoreFiles(items.map(item => item.name));
      setSelectedStoreFile('');
      setSelectedBucket(targetBucket);
    } catch (error) {
      setErrorMessage(`Failed to initialize store connection: ${error.message}`);
    }
  }, [selectedBucket]);

  useEffect(() => {
    const currentOrigin = window.location.origin;
  
    const callbackUrl = (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) 
      ? import.meta.env.VITE_CALLBACK_URL_LOCAL 
      : import.meta.env.VITE_CALLBACK_URL;

    if (callbackUrl && !callbackUrl.startsWith(currentOrigin)) {
      console.warn('Callback URL mismatch:', {
        currentOrigin,
        callbackUrl,
        suggestion: 'This may cause OAuth issues in local development'
      });
      
      if (currentOrigin.includes('localhost')) {
        setErrorMessage(
          'Local development detected with cloud OAuth configuration. ' +
          'Please ensure local URI is added to Google Cloud OAuth consent screen.'
        );
      }
    }

    if (!import.meta.env.VITE_BACKEND_URL) {
      console.error('VITE_BACKEND_URL is not defined');
      setErrorMessage('Backend configuration missing. Please check environment variables.');
    }
  }, []);

  const handleTabSelectAndImport = async (tabName) => {
    if (!selectedSheet || !tabName) {
      setErrorMessage('Please select a Google Sheet and a tab to import.');
      return;
    }
    
    if (!hasDriveAccess()) {
      setErrorMessage('Access denied. Only Admin and Super users can access Google Drive.');
      return;
    }

    try {
      const data = await fetchSheetData(selectedSheet.id, tabName);
      
      if (data.length === 0) {
        setErrorMessage('No data found in the selected tab.');
        return;
      }
      
      const nonBlankValues = data.filter(row => !row.every(cell => !cell || cell.toString().trim() === ''));
      if (nonBlankValues.length === 0) {
        setErrorMessage('No data found in the selected tab after filtering out blank rows.');
        return;
      }
      
      const headers = nonBlankValues[0];
      
      const headerCount = {};
      const uniqueHeaders = headers.map((header, index) => {
        if (!header || header.trim() === '') {
          return `Column_${index + 1}`;
        }
        
        const baseHeader = header.trim();
        if (headerCount[baseHeader] === undefined) {
          headerCount[baseHeader] = 1;
          return baseHeader;
        } else {
          headerCount[baseHeader]++;
          return `${baseHeader}_${headerCount[baseHeader]}`;
        }
      });
      
      const processedData = nonBlankValues.slice(1).map((row, rowIndex) => {
        const rowData = {};
        uniqueHeaders.forEach((header, index) => {
          const value = index < row.length ? row[index] : '';
          rowData[header] = value || '';
        });
        return rowData;
      });
      
      const { expandedHeaders, expandedData } = expandArrayColumns(processedData);
      setFileData(expandedData);
      setFilteredFileData(expandedData);
      
      if (expandedData.length > 0) {
        setHeaders(expandedHeaders);
        setOriginalHeaders(expandedHeaders);
        setSelectedColumns(expandedHeaders);
        setHeaderMapping({}); 
      } else {
        setHeaders([]);
        setOriginalHeaders([]);
        setSelectedColumns([]);
      }

      setShowConnectedMessage(true);
      setSelectedRecords([]);
      setSelectAll(false);
      setSearchQuery('');
      setOtemplateDocs([]);
      setSelectedOtemplateDoc(null);

    } catch (error) {
      console.error('Error importing data from Google Sheet:', error);
      setErrorMessage('Failed to import data from the selected Google Sheet tab.');
    }
  };

  // Access control
  useEffect(() => {
    if (!['Admin', 'Super'].includes(userRole)) {
      setErrorMessage('Access denied. You must be an Admin or Super user.');
      navigate('/login', { replace: true });
      return;
    }
  }, [userRole, navigate]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      blobUrlRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlRef.current.clear();
    };
  }, []);

  // Reset column editing when data changes
  useEffect(() => {
    if (editingHeaderIndex !== null) {
      setEditingHeaderIndex(null);
      setEditingHeaderValue('');
    }
  }, [headers, activeConnection]);

  // Hide connected message after timeout
  useEffect(() => {
    if (showConnectedMessage) {
      const timer = setTimeout(() => {
        setShowConnectedMessage(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showConnectedMessage]);

  // Connection handler
  const handleConnectOption = (option) => {
    const isSwitching = activeConnection !== option;

    setIsConnectMenuOpen(false);

    // Always clear grid data (whether switching or refreshing same connection)
    setSelectedTable(null);
    setTableData([]);
    setFilteredTableData([]);
    setSelectedFile(null);
    setFileData([]);
    setFilteredFileData([]);
    resetSelections();
    setSelectedRecords([]);
    setSelectAll(false);
    setHeaders([]);
    setSelectedColumns([]);
    setSearchQuery('');

    // Only reset connection-level state when switching connection types
    if (isSwitching) {
      setSortConfig({ key: null, direction: 'asc' });
      setOtemplateDocs([]);
      setSelectedOtemplateDoc(null);
      setSelectedSheet(null);
      setSheetTabs([]);
      setSelectedTab(null);
      setCollections([]);
      setSelectedFolder('');
      setStoreFiles([]);
      setSelectedStoreFile('');
      setSelectedForm(null);
      setFormResponseData([]);
      setHeaderMapping({});
    }

    setActiveConnection(option);
    setErrorMessage(null);
    setDriveErrorMessage(null);
    setShowConnectedMessage(false);

    if (option === 'db') {
      setIsMediaFile(false);
      setFileType('data');
      establishDbConnection()
        .then(() => fetchFirestoreCollections())
        .then((firestoreColls) => {
          if (firestoreColls.length === 0) {
            setErrorMessage('No accessible Firestore collections found.');
          }
          setFirestoreCollections(firestoreColls);
          fetchCollections();
          setShowConnectedMessage(true);
        })
        .catch(err => {
          console.error('Failed to connect to database:', err);
          setErrorMessage(`Failed to connect to database: ${err.message}`);
        });
    } else if (option === 'file') {
      setShowConnectedMessage(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fileInputRef.current.click();
    } else if (option === 'drive') {
      setShowConnectedMessage(true);
      handleGoogleDriveConnect();
    } else if (option === 'store') {
      setShowConnectedMessage(true);
      initializeStoreConnection();
    }
  };

  const handleGoogleDriveConnect = async () => {
    if (!hasDriveAccess()) {
      setErrorMessage('Access denied.');
      return;
    }

    if (isDriveConnected && activeConnection === 'drive') {
      setShowConnectedMessage(true);
      return;
    }

    if (!isDriveConnected) {
      try {
        const result = await connectToGoogleDrive();
        if (!result?.success) {
          setDriveErrorMessage(result?.error || 'Connection failed');
          return;
        }
      } catch (err) {
        setDriveErrorMessage(`Drive error: ${err.message}`);
        return;
      }
    }

    setActiveConnection('drive');
    setShowConnectedMessage(true);
    setSelectedTable(null);
    setTableData([]);
    setFilteredTableData([]);
    setSelectedFile(null);
  };

  // Add useEffect to clear drive errors when switching away from drive
  useEffect(() => {
    if (activeConnection !== 'drive') {
      setDriveErrorMessage(null);
    }
  }, [activeConnection]);


  // Fetch sheet tabs using context
  const fetchSheetTabs = async (sheetId) => {
    if (!hasDriveAccess()) {
      setErrorMessage('Access denied. Only Admin and Super users can access Google Drive.');
      return;
    }

    try {
      const metadata = await fetchSheetMetadata(sheetId);
      const tabs = metadata.sheets.map(sheet => sheet.properties.title);
      setSheetTabs(tabs);
      setSelectedTab(null);
    } catch (error) {
      console.error('Error fetching sheet tabs:', error);
      setErrorMessage(`Failed to fetch sheet tabs: ${error.message}`);
      setSheetTabs([]);
      setSelectedTab(null);
    }
  };

  // Handle sheet selection
  const handleSheetSelect = (sheetId) => {
    const sheet = googleSheets.find(s => s.id === sheetId);
    setSelectedSheet(sheet);
    setSheetTabs([]);
    setSelectedTab(null);
    setSelectedForm(null);
    setFormResponseData([]);
    if (sheet) {
      fetchSheetTabs(sheet.id);
    }
  };

  // Handle form selection
  const handleFormSelect = async (formId) => {
    const form = googleForms.find(f => f.id === formId);
    setSelectedForm(form);
    setSelectedSheet(null); // Clear sheet selection
    setSheetTabs([]);
    setSelectedTab(null);
    
    try {
      // Fetch form details and responses like in Enquiry
      const [formDetails, responses] = await Promise.all([
        fetchFormDetails(formId),
        fetchFormResponses(formId)
      ]);

      // Helper function to extract answer value
      const getAnswerValue = (answer) => {
        if (!answer) return '';
        
        if (answer.textAnswers) {
          return answer.textAnswers.answers?.map(a => a.value).join(', ') || '';
        } else if (answer.fileUploadAnswers) {
          const fileIds = answer.fileUploadAnswers.answers?.map(a => a.fileId) || [];
          // Convert file IDs to clickable links
          return fileIds.map(fileId => 
            `📎 <a href="https://drive.google.com/file/d/${fileId}/view" target="_blank" style="color: #2196f3; text-decoration: underline;">
              View File
            </a>`
          ).join(', ') || '[No files]';
        } else if (answer.choiceAnswers) {
          return answer.choiceAnswers.answers?.map(a => a.value).join(', ') || '';
        } else if (answer.dateAnswers) {
          return answer.dateAnswers.answers?.map(a => a.value).join(', ') || '';
        } else if (answer.timeAnswers) {
          return answer.timeAnswers.answers?.map(a => a.value).join(', ') || '';
        }
        
        return '';
      };

      // Create mapping from questionId to column name
      const columnNameMap = new Map();
      const questionTitleCount = {};
      
      if (formDetails.items) {
        formDetails.items.forEach((item, index) => {
          let questionId = null;
          
          // Extract questionId from different possible structures
          if (item.questionItem?.question?.questionId) {
            questionId = item.questionItem.question.questionId;
          } else if (item.question?.questionId) {
            questionId = item.question.questionId;
          } else if (item.itemId) {
            questionId = item.itemId;
          }
          
          if (!questionId) {
            console.warn('Could not find questionId for item:', item);
            return;
          }
          
          let columnName = item.title || `question_${questionId}`;
          
          // Clean up column name
          columnName = columnName.trim();
          if (!columnName) columnName = `question_${questionId}`;
          
          // Handle duplicate column names
          const originalColumnName = columnName;
          if (questionTitleCount[originalColumnName]) {
            questionTitleCount[originalColumnName]++;
            columnName = `${originalColumnName}_${questionTitleCount[originalColumnName]}`;
          } else {
            questionTitleCount[originalColumnName] = 1;
          }
          
          columnNameMap.set(questionId, columnName);
        });
      }

      // Map all responses
      const mappedResponses = responses.map((response, responseIndex) => {
        const answers = response.answers || {};
        const formsApiId = response.responseId;
        
        const row = {
          id: `form_${responseIndex}_${formsApiId}`,
          responseId: response.responseId, // Keep Forms API ID for reference
          formsApiId: formsApiId, // Keep original for reference
          createTime: response.createTime,
          lastSubmittedTime: response.lastSubmittedTime,
          respondentEmail: response.respondentEmail || '',
        };
        
        // Add all columns from the map
        columnNameMap.forEach((columnName, questionId) => {
          if (answers[questionId]) {
            row[columnName] = getAnswerValue(answers[questionId]);
          } else {
            row[columnName] = '';
          }
        });

        return row;
      });
            
      if (mappedResponses.length > 0) {
        const firstRow = mappedResponses[0];
        const headers = Object.keys(firstRow);
        const columnsWithData = headers.filter(h => 
          h !== 'id' && h !== 'responseId' && h !== 'createTime' && 
          h !== 'lastSubmittedTime' && h !== 'respondentEmail'
        );
      }

      // Set state
      if (mappedResponses.length > 0) {
        const headers = Object.keys(mappedResponses[0]);
        
        // Filter out empty columns (where all values are empty)
        const nonEmptyHeaders = headers.filter(header => {
        // Skip metadata columns that come before createTime
        if (['id', 'responseId', 'formsApiId'].includes(header)) {
          return false; // Don't include these in display
        }
        
        // Always include these metadata columns for display
        if (['createTime', 'lastSubmittedTime', 'respondentEmail'].includes(header)) {
          return true;
        }
        
        // Check if any row has non-empty value for this header
        return mappedResponses.some(row => row[header] && row[header].trim() !== '');
      });
        
        setFormResponseData(mappedResponses);
        setFileData(mappedResponses);
        setFilteredFileData(mappedResponses);
        setHeaders(nonEmptyHeaders);
        setOriginalHeaders(nonEmptyHeaders);
        setSelectedColumns(nonEmptyHeaders);
        setHeaderMapping({});
      } else {
        setFormResponseData([]);
        setFileData([]);
        setFilteredFileData([]);
        setHeaders([]);
        setOriginalHeaders([]);
        setSelectedColumns([]);
      }

      setIsMediaFile(false);
      setFileType('data');
      setShowConnectedMessage(true);

    } catch (error) {
      console.error('Error fetching form data:', error);
      setErrorMessage(`Failed to load form data: ${error.message}`);
    }
  }; 

 
  const launchSaveModal = (dataToSave = null) => {
    const dataToUse = dataToSave || fileDataToSave;
    
    let mode;
    if (isMediaFile) {
      mode = 'bucket'; // Media files always go to bucket
    } else if (fileFormat === 'bucket') {
      mode = 'bucket'; // Explicit bucket format selection
    } else if (fileFormat === 'document') {
      mode = 'collection'; // Firestore document
    } else {
      mode = 'file'; // CSV/Excel download
    }
    
    const config = createSaveModalConfig(mode, {
      fileDataToSave: dataToUse, 
      fileFormat,
      isMediaFile,
      fileType,
      selectedFile,
      selectedStoreFile,
      activeConnection,
      selectedBucket,
      currentPath,
      fetchAvailableBuckets,
      fetchStoreFiles,
      fetchFirestoreCollections,
      setIsBucketModalOpen: () => setActiveModalConfig(null),
      setIsFileModalOpen: () => setActiveModalConfig(null), 
      setIsFirestoreSaveModalOpen: () => setActiveModalConfig(null),
      setIsSaveFileModalOpen: () => setActiveModalConfig(null),
      availableBuckets,
      setFileDataToSave,
    });
    setActiveModalConfig(config);
  };


  
  const fetchStoreFiles = async (folderPath = '', bucketName = '') => {
    try {
      const targetBucket = bucketName || selectedBucket;
      const result = await listObjectsInFolder(folderPath, targetBucket);
      const { items, prefixes } = result;

      // Update folder hierarchy
      const folderStructure = buildFolderHierarchy(prefixes);
      setFolderHierarchy(folderStructure);
      setCurrentPath(folderPath);
     
      const files = items.map(item => item.name);
      setStoreFiles(files);
     
      // Build breadcrumb path
      const pathParts = folderPath.split('/').filter(part => part);
      setFolderHierarchy(prev => [...prev]); // Trigger re-render
     
    } catch (error) {
      setErrorMessage(`Failed to list files in ${folderPath || 'root'}: ${error.message}`);
    }
  };
  // Recursive folder component
  const FolderTree = ({ folders, currentPath, onFolderSelect, level = 0 }) => {
    if (!folders || folders.length === 0) return null;
    return (
      <div style={{ marginLeft: level > 0 ? '15px' : '0' }}>
        {folders.map((folder, index) => (
          <div key={index}>
            <div
              className="hk-folder-item"
              style={{
                padding: '2px 5px',
                cursor: 'pointer',
                borderRadius: '3px',
                margin: '1px 0',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
              }}
              data-selected={currentPath === folder.fullPath ? 'true' : undefined}
              onClick={() => onFolderSelect(folder.fullPath)}
              title={folder.fullPath}
            >
              <span style={{ marginRight: '5px' }}>📁</span>
              {folder.name}
            </div>
            {folder.children && folder.children.length > 0 && (
              <FolderTree
                folders={folder.children}
                currentPath={currentPath}
                onFolderSelect={onFolderSelect}
                level={level + 1}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const resetSelections = useCallback(() => {
    setSelectedRecords([]);
    setSelectAll(false);
    
    // For column selection, use either current headers or all available headers
    const currentHeaders = activeConnection === 'db' 
      ? (filteredTableData.length > 0 ? Object.keys(filteredTableData[0] || {}) : [])
      : (filteredFileData.length > 0 ? Object.keys(filteredFileData[0] || {}) : []);
    
    if (currentHeaders.length > 0) {
      setSelectedColumns(currentHeaders);
      setSelectAllColumns(true);
    } else if (headers.length > 0) {
      setSelectedColumns(headers);
      setSelectAllColumns(true);
    }
  }, [activeConnection, filteredTableData, filteredFileData, headers]);

  const handleStoreFileSelect = useCallback(async (fileName, retryCount = 0) => {
    const maxRetries = 2;
   
    try {
      setSelectedStoreFile(fileName);
      
      if (!fileName || fileName.trim() === '') {
        throw new Error('File path is required');
      }
      
      // Reset selections BEFORE loading new data
      resetSelections();
      setErrorMessage(null);
      setDriveErrorMessage(null);
      setIsMediaFile(false);
      setMediaPreviewUrl('');
      setMediaFiles([]);
      setFileData([]);
      setFilteredFileData([]);
      setHeaders([]);
      setOriginalHeaders([]);
      setSelectedColumns([]);

      const result = await readObjectContent(fileName, selectedBucket);
      const videoFormats = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp'];
      const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const documentFormats = ['pdf', 'doc', 'docx', 'txt'];
     
      const fileExt = fileName.split('.').pop().toLowerCase();
      const isVideo = videoFormats.includes(fileExt);
      const isImage = imageFormats.includes(fileExt);
      const isPdf = fileExt === 'pdf';
      const isOtherDocument = documentFormats.includes(fileExt) && !isPdf;
      const isMedia = isVideo || isImage || isPdf || isOtherDocument;
     
      if (isMedia) {
        const mediaType = isImage ? 'image' : isVideo ? 'video' : isPdf ? 'pdf' : 'document';     
        const previewUrl = result.downloadUrl;
       
        setMediaPreviewUrl(previewUrl);
        setMediaFiles([{
          name: fileName,
          previewUrl: previewUrl,
          type: mediaType,
          size: result.size || 'N/A',
          originalUrl: result.downloadUrl
        }]);
       
        const fileData = [{
          name: fileName,
          type: mediaType,
          size: formatFileSize(result.size || 0),
          preview: previewUrl
        }];
       
        setFileData(fileData);
        setFilteredFileData(fileData);
        setHeaders(['preview', 'name', 'type', 'size']);
        setSelectedColumns(['preview', 'name', 'type', 'size']);
        setShowConnectedMessage(true);
       
        setIsMediaFile(true);
        setFileType(mediaType);
       
      } else {
        // Handle data files (CSV, JSON, Excel) - existing code remains the same
        const content = result;
       
        if (fileName.endsWith('.csv')) {
          Papa.parse(content, {
            header: true,
            complete: (result) => {
              const data = result.data.filter(row => Object.keys(row).length > 0);
              const { expandedHeaders, expandedData } = expandArrayColumns(data);
              setFileData(expandedData);
              setFilteredFileData(expandedData);
              if (expandedData.length > 0) {
                setHeaders(expandedHeaders);
                setOriginalHeaders(expandedHeaders);
                setSelectedColumns(expandedHeaders);
                setHeaderMapping({});
              } else {
                console.warn('No valid data found in CSV file');
                setHeaders([]);
                setOriginalHeaders([]);
                setSelectedColumns([]);
              }
              setShowConnectedMessage(true);
              setIsMediaFile(false);
              setFileType('data');
            },
            error: (err) => {
              console.error('Failed to parse CSV:', err);
              if (retryCount < maxRetries) {
                setTimeout(() => {
                  handleStoreFileSelect(fileName, retryCount + 1);
                }, 500);
              } else {
                setErrorMessage(`Failed to parse CSV ${fileName}: ${err.message}`);
              }
            },
          });
        } else if (fileName.endsWith('.json')) {
          try {
            const json = JSON.parse(content);
            const data = Array.isArray(json) ? json : [];
            const { expandedHeaders, expandedData } = expandArrayColumns(data);
            setFileData(expandedData);
            setFilteredFileData(expandedData);
            if (expandedData.length > 0) {
              setHeaders(expandedHeaders);
              setOriginalHeaders(expandedHeaders);
              setSelectedColumns(expandedHeaders);
              setHeaderMapping({});
            } else {
              console.warn('No valid data found in JSON file');
              setHeaders([]);
              setOriginalHeaders([]);
              setSelectedColumns([]);
            }
            setShowConnectedMessage(true);
            setIsMediaFile(false);
            setFileType('data');
          } catch (err) {
            console.error('Failed to parse JSON:', err);
            if (retryCount < maxRetries) {
              setTimeout(() => {
                handleStoreFileSelect(fileName, retryCount + 1);
              }, 500);
            } else {
              setErrorMessage(`Failed to parse JSON ${fileName}: ${err.message}`);
            }
          }
        } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          try {
            const workbook = new ExcelJS.Workbook();
          
            let excelData;
            if (typeof content === 'string') {
              // Convert base64 string to ArrayBuffer
              if (content.startsWith('data:')) {
                // If it's a data URL, extract the base64 part
                const base64 = content.split(',')[1];
                const binaryString = atob(base64);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                excelData = bytes.buffer;
              } else {
                // Assume it's base64 encoded string
                try {
                  const binaryString = atob(content);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  excelData = bytes.buffer;
                } catch (e) {
                  // If not base64, treat as binary string
                  const encoder = new TextEncoder();
                  excelData = encoder.encode(content).buffer;
                }
              }
            } else if (content instanceof ArrayBuffer) {
              excelData = content;
            } else {
              throw new Error('Unsupported content type for Excel file');
            }
            
            await workbook.xlsx.load(excelData);
            const worksheet = workbook.worksheets[0];
            const json = [];
            const headers = [];
            
            // Get headers from first row
            worksheet.getRow(1).eachCell((cell) => {
              headers.push(cell.value || '');
            });
            
            // Get data rows
            worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber === 1) return; // Skip header row
              const rowData = {};
              row.eachCell((cell, colNumber) => {
                rowData[headers[colNumber - 1]] = cell.value !== null && cell.value !== undefined ? cell.value : '';
              });
              json.push(rowData);
            });
          
            const { expandedHeaders, expandedData } = expandArrayColumns(json);
            setFileData(expandedData);
            setFilteredFileData(expandedData);
            if (expandedData.length > 0) {
              setHeaders(expandedHeaders);
              setOriginalHeaders(expandedHeaders);
              setSelectedColumns(expandedHeaders);
              setHeaderMapping({});
            } else {
              console.warn('No valid data found in Excel file');
              setHeaders([]);
              setOriginalHeaders([]);
              setSelectedColumns([]);
            }
            setShowConnectedMessage(true);
            setIsMediaFile(false);
            setFileType('data');
          } catch (err) {
            console.error('Error parsing Excel file:', err);
            
            // Alternative approach: Try using the download URL directly
            if (retryCount === 0 && result.downloadUrl) {
              try {
                const response = await fetch(result.downloadUrl);
                if (!response.ok) throw new Error('Failed to fetch Excel file');
                
                const arrayBuffer = await response.arrayBuffer();
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(arrayBuffer);
                
                const worksheet = workbook.worksheets[0];
                const json = [];
                const headers = [];
                
                worksheet.getRow(1).eachCell((cell) => {
                  headers.push(cell.value || '');
                });
                
                worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
                  if (rowNumber === 1) return;
                  const rowData = {};
                  row.eachCell((cell, colNumber) => {
                    rowData[headers[colNumber - 1]] = cell.value !== null && cell.value !== undefined ? cell.value : '';
                  });
                  json.push(rowData);
                });
                
                const { expandedHeaders, expandedData } = expandArrayColumns(json);
                setFileData(expandedData);
                setFilteredFileData(expandedData);
                if (expandedData.length > 0) {
                  setHeaders(expandedHeaders);
                  setOriginalHeaders(expandedHeaders);
                  setSelectedColumns(expandedHeaders);
                }
                setShowConnectedMessage(true);
                setIsMediaFile(false);
                setFileType('data');
                return;
              } catch (fallbackError) {
                console.error('Fallback Excel loading also failed:', fallbackError);
              }
            }
            
            if (retryCount < maxRetries) {
              setTimeout(() => {
                handleStoreFileSelect(fileName, retryCount + 1);
              }, 500);
            } else {
              setErrorMessage(`Failed to parse Excel ${fileName}: ${err.message}`);
            }
          }
        } else {
          // Unsupported file format
          if (retryCount < maxRetries) {
            setTimeout(() => {
              handleStoreFileSelect(fileName, retryCount + 1);
            }, 500);
          } else {
            setErrorMessage(`Unsupported file format for ${fileName}`);
          }
        }
      }
    } catch (error) {
      console.error(`Error reading file (attempt ${retryCount + 1}):`, error);
     
      if (retryCount < maxRetries) {
        setTimeout(() => {
          handleStoreFileSelect(fileName, retryCount + 1);
        }, 500);
      } else {
        setErrorMessage(`Failed to read ${fileName} after ${maxRetries + 1} attempts: ${error.message}`);
      }
    }
  }, [selectedBucket, resetSelections]);

  useEffect(() => {
    if (activeConnection === 'store') {
      initializeStoreConnection();
    }
  }, [activeConnection, initializeStoreConnection]);

  useEffect(() => {
    return () => {
      blobUrlRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlRef.current.clear();
    };
  }, []);

  const parseDateValue = (value) => {
  if (typeof value !== 'string') return null;
  const str = value.trim();
  if (str === '') return null;

  // ISO format (fast path)
  const isoDate = new Date(str);
  if (!isNaN(isoDate.getTime()) && str.includes('T')) {
    return isoDate.getTime();
  }

  // German format: DD.MM.YYYY or DD.MM.YYYY HH:MM:SS
  const germanRegex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
  const germanMatch = str.match(germanRegex);
  if (germanMatch) {
    const day = parseInt(germanMatch[1], 10);
    const month = parseInt(germanMatch[2], 10) - 1;
    const year = parseInt(germanMatch[3], 10);
    const hour = germanMatch[4] ? parseInt(germanMatch[4], 10) : 0;
    const minute = germanMatch[5] ? parseInt(germanMatch[5], 10) : 0;
    const second = germanMatch[6] ? parseInt(germanMatch[6], 10) : 0;
    const date = new Date(year, month, day, hour, minute, second);
    if (!isNaN(date.getTime())) return date.getTime();
  }

  // US format: MM/DD/YYYY or MM/DD/YYYY HH:MM:SS
  const usRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
  const usMatch = str.match(usRegex);
  if (usMatch) {
    const month = parseInt(usMatch[1], 10) - 1;
    const day = parseInt(usMatch[2], 10);
    const year = parseInt(usMatch[3], 10);
    const hour = usMatch[4] ? parseInt(usMatch[4], 10) : 0;
    const minute = usMatch[5] ? parseInt(usMatch[5], 10) : 0;
    const second = usMatch[6] ? parseInt(usMatch[6], 10) : 0;
    const date = new Date(year, month, day, hour, minute, second);
    if (!isNaN(date.getTime())) return date.getTime();
  }

  // DB collection format: "DD Mon YYYY at HH:MM:SS"
  const dbFormatRegex = /(\d{1,2}) (\w{3,}) (\d{4}) at (\d{1,2}):(\d{2}):(\d{2})/i;
    const dbMatch = str.match(dbFormatRegex);
    if (dbMatch) {
      const day = parseInt(dbMatch[1], 10);
      const monthStr = dbMatch[2];
      const year = parseInt(dbMatch[3], 10);
      const hour = parseInt(dbMatch[4], 10);
      const minute = parseInt(dbMatch[5], 10);
      const second = parseInt(dbMatch[6], 10);
      const monthNames = {
        jan:0, january:0, feb:1, february:1, mar:2, march:2,
        apr:3, april:3, may:4, jun:5, june:5, jul:6, july:6,
        aug:7, august:7, sep:8, september:8, oct:9, october:9,
        nov:10, november:10, dec:11, december:11
      };
      const month = monthNames[monthStr.toLowerCase()];
      if (month !== undefined) {
        const date = new Date(year, month, day, hour, minute, second);
        if (!isNaN(date.getTime())) return date.getTime();
      }
    }

    // Fallback to generic Date parsing
    const genericDate = new Date(str);
    if (!isNaN(genericDate.getTime())) return genericDate.getTime();

    return null; // not a recognized date
  };

  const fetchCollections = async () => {
    try {
      if (firestoreCollections.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return;
      }
      const collectionData = firestoreCollections.map(coll => ({
        name: coll,
        fields: coll === 'otemplate' ? [] : null,
      }));
      if (collectionData.length === 0) {
        setErrorMessage('No tables found. Check permissions.');
      } else {
        setCollections(collectionData);
      }
    } catch (err) {
      console.error('Error initializing collections:', err);
      setErrorMessage(`Failed to initialize tables: ${err.message}`);
    }
  };
  const handleTableSelect = async (tableName) => {
    const table = collections.find(coll => coll.name === tableName);
    if (table) {
      setSelectedTable(table);
      setIsMediaFile(false);
      setFileType('data');
      if (tableName === 'otemplate') {
        try {
          const docIds = await fetchCollectionDocIds('otemplate');
          if (docIds.length > 0) {
            setOtemplateDocs(docIds);
            setTableData([]);
            setFilteredTableData([]);
            setHeaders([]);
            setOriginalHeaders([]);
            setSelectedColumns([]);
            setSelectedRecords([]);
            setSelectAll(false);
          } else {
            setErrorMessage('No documents found in otemplate.');
            setOtemplateDocs([]);
          }
        } catch (err) {
          setErrorMessage(`Failed to fetch otemplate document IDs: ${err.message}`);
          setOtemplateDocs([]);
          console.error('Error fetching otemplate docIds:', err);
        }
      } else {
        try {
          const data = await readFirestoreCollection(tableName);
          const formattedData = data.map(doc => ({ id: doc.id, ...doc }));
          const { expandedHeaders, expandedData } = expandArrayColumns(formattedData);
          setTableData(expandedData);
          setFilteredTableData(expandedData);
          if (expandedData.length > 0) {
            setHeaders(expandedHeaders);
            setOriginalHeaders(expandedHeaders);
            setSelectedColumns(expandedHeaders);
            setHeaderMapping({});
          } else {
            setHeaders([]);
            setOriginalHeaders([]);
            setSelectedColumns([]);
          }
          setSelectedRecords([]);
          setSelectAll(false);
          setSortConfig({ key: null, direction: 'asc' });
          setSearchQuery('');
          setOtemplateDocs([]);
          setSelectedOtemplateDoc(null);
        } catch (err) {
          setErrorMessage(err.message);
          console.error('Error fetching table data:', err);
        }
      }
    }
  };

  const handleOtemplateDocSelect = async (docId) => {
    setSelectedOtemplateDoc(docId);
    try {
      const docData = await fetchDocumentById('otemplate', docId);
      if (docData) {
        const data = [{ id: docId, ...docData }];
        const { expandedHeaders, expandedData } = expandArrayColumns(data);
        setTableData(expandedData);
        setFilteredTableData(expandedData);
        if (expandedData.length > 0) {
          setHeaders(expandedHeaders);
          setOriginalHeaders(expandedHeaders);
          setSelectedColumns(expandedHeaders);
          setHeaderMapping({});
        } else {
          setHeaders([]);
          setOriginalHeaders([]);
          setSelectedColumns([]);
        }
        setSelectedRecords([]);
        setSelectAll(false);
        setSortConfig({ key: null, direction: 'asc' });
        setSearchQuery('');
      } else {
        setErrorMessage(`Document ${docId} not found in otemplate collection.`);
        setTableData([]);
        setFilteredTableData([]);
        setHeaders([]);
        setOriginalHeaders([]);
        setSelectedColumns([]);
      }
    } catch (err) {
      setErrorMessage(err.message);
      setTableData([]);
      setFilteredTableData([]);
      setHeaders([]);
      setOriginalHeaders([]);
      setSelectedColumns([]);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    
    setSelectedFile(file);
    setIsMediaFile(false);
    setMediaFiles([]);
    setMediaPreviewUrl('');
    setFileData([]);
    setFilteredFileData([]);
    setHeaders([]);
    setOriginalHeaders([]);
    setSelectedColumns([]);
    setSearchQuery('');
    setErrorMessage(null);
    setActiveConnection('file');
    setShowConnectedMessage(true);

    blobUrlRef.current.forEach(url => {
      URL.revokeObjectURL(url);
    });
    blobUrlRef.current.clear();

    const fileName = file.name.toLowerCase();
    const isImage = fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/);
    const isVideo = fileName.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/);
    const isPdf = fileName.match(/\.pdf$/);
    const isOtherDocument  = fileName.match(/\.(doc|docx|txt)$/);
    const isMedia = isImage || isVideo || isPdf || isOtherDocument;
    
    const detectedFileType = isImage ? 'image' : isVideo ? 'video' : isPdf ? 'pdf': isOtherDocument ? 'document' : 'data';
    setFileType(detectedFileType);
    setIsMediaFile(isMedia);

    if (isMedia) {
      // Create blob URL and track it
      const previewUrl = URL.createObjectURL(file);
      
      // Track this blob URL for cleanup
      blobUrlRef.current.add(previewUrl);
      
      setMediaPreviewUrl(previewUrl);
      setMediaFiles([{
        file,
        previewUrl,
        type: detectedFileType,
        name: file.name,
        size: file.size
      }]);
      
      const fileData = [{
        preview: previewUrl,
        name: file.name,
        type: detectedFileType,
        size: formatFileSize(file.size)
      }];
      
      setTimeout(() => {
        setFileData(fileData);
        setFilteredFileData(fileData);
        setHeaders(['preview', 'name', 'type', 'size']);
        setSelectedColumns(['preview', 'name', 'type', 'size']);
      }, 0);
      
    } else {
      resetSelections();
      
      if (file.name.endsWith('.csv')) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const data = result.data.filter(row => Object.keys(row).length > 0 && !Object.values(row).every(val => !val));
            
            if (data.length > 0) {
              const { expandedHeaders, expandedData } = expandArrayColumns(data);
              // Use setTimeout to ensure React batches state updates properly
              setTimeout(() => {
                setFileData(expandedData);
                setFilteredFileData(expandedData);
                if (expandedData.length > 0) {
                  setHeaders(expandedHeaders);
                  setOriginalHeaders(expandedHeaders);
                  setSelectedColumns(expandedHeaders);
                  setHeaderMapping({});

                } else {
                  console.warn('No valid data found in CSV file after expansion');
                  setHeaders([]);
                  setOriginalHeaders([]);
                  setSelectedColumns([]);
                }
              }, 0);
            } else {
              console.warn('No valid data found in CSV file');
              setHeaders([]);
              setOriginalHeaders([]);
              setSelectedColumns([]);
              setFileData([]);
              setFilteredFileData([]);
            }
          },
          error: (err) => {
            console.error('Error parsing CSV:', err);
            setErrorMessage(`Failed to parse CSV file: ${err.message}`);
          },
        });
      } else if (file.name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const json = JSON.parse(event.target.result);
            const data = Array.isArray(json) ? json : [json];
            
            if (data.length > 0) {
              const { expandedHeaders, expandedData } = expandArrayColumns(data);
              setTimeout(() => {
                setFileData(expandedData);
                setFilteredFileData(expandedData);
                if (expandedData.length > 0) {
                  setHeaders(expandedHeaders);
                  setOriginalHeaders(expandedHeaders);
                  setSelectedColumns(expandedHeaders);
                  setHeaderMapping({});
                } else {
                  console.warn('No valid data found in JSON file after expansion');
                  setHeaders([]);
                  setOriginalHeaders([]);
                  setSelectedColumns([]);
                }
              }, 0);
            } else {
              console.warn('No valid data found in JSON file');
              setHeaders([]);
              setOriginalHeaders([]);
              setSelectedColumns([]);
              setFileData([]);
              setFilteredFileData([]);
            }
          } catch (err) {
            console.error('Error parsing JSON:', err);
            setErrorMessage(`Failed to parse JSON file: ${err.message}`);
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          setErrorMessage('Failed to read the file. Please try again.');
        };
        reader.readAsText(file);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(event.target.result);
            const worksheet = workbook.worksheets[0];
            const json = [];
            const headers = [];
            
            // Get headers from first row
            worksheet.getRow(1).eachCell((cell) => {
              headers.push(cell.value || '');
            });
            
            // Get data rows
            worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber === 1) return; 
              const rowData = {};
              row.eachCell((cell, colNumber) => {
                rowData[headers[colNumber - 1]] = cell.value !== null && cell.value !== undefined ? cell.value : '';
              });
              json.push(rowData);
            });
            
            if (json.length > 0) {
              const { expandedHeaders, expandedData } = expandArrayColumns(json);
              setTimeout(() => {
                setFileData(expandedData);
                setFilteredFileData(expandedData);
                if (expandedData.length > 0) {
                  setHeaders(expandedHeaders);
                  setOriginalHeaders(expandedHeaders);
                  setSelectedColumns(expandedHeaders);
                  setHeaderMapping({});
                } else {
                  console.warn('No valid data found in Excel file after expansion');
                  setHeaders([]);
                  setOriginalHeaders([]);
                  setSelectedColumns([]);
                }
              }, 0);
            } else {
              console.warn('No valid data found in Excel file');
              setHeaders([]);
              setOriginalHeaders([]);
              setSelectedColumns([]);
              setFileData([]);
              setFilteredFileData([]);
            }
          } catch (err) {
            console.error('Error parsing Excel file:', err);
            setErrorMessage(`Failed to parse Excel file: ${err.message}`);
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading Excel file:', error);
          setErrorMessage('Failed to read the Excel file. Please try again.');
        };
        reader.readAsArrayBuffer(file);
      } else {
        console.warn('Unsupported file format:', file.name);
        setErrorMessage('Unsupported file format. Please upload a CSV, JSON, or Excel file.');
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });

    const dataToSort = activeConnection === 'db' ? [...filteredTableData] : [...filteredFileData];
    
    dataToSort.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      // Attempt to parse as dates
      const aTime = typeof aValue === 'string' ? parseDateValue(aValue) : null;
      const bTime = typeof bValue === 'string' ? parseDateValue(bValue) : null;

      // If both are valid dates, compare timestamps
      if (aTime !== null && bTime !== null) {
        if (aTime < bTime) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aTime > bTime) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }

      // Otherwise fall back to string/number comparison
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    if (activeConnection === 'db') {
      setFilteredTableData(dataToSort);
    } else {
      setFilteredFileData(dataToSort);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
      setSelectAll(false);
    } else {
      const allRecords = (activeConnection === 'db' ? filteredTableData : filteredFileData).map((_, index) => index);
      setSelectedRecords(allRecords);
      setSelectAll(true);
    }
  };

  const handleSelectRecord = (index) => {
    if (selectedRecords.includes(index)) {
      setSelectedRecords(selectedRecords.filter(i => i !== index));
      setSelectAll(false);
    } else {
      const newSelection = [...selectedRecords, index];
      setSelectedRecords(newSelection);
      if (newSelection.length === (activeConnection === 'db' ? filteredTableData : filteredFileData).length) {
        setSelectAll(true);
      }
    }
  };

  const handleSelectAllColumns = () => {
    if (selectAllColumns) {
      // Deselect all columns
      setSelectedColumns([]);
      setSelectAllColumns(false);
    } else {
      // Select all columns (excluding 'preview' for media files)
      const allColumns = isMediaFile ? 
        headers.filter(header => header !== 'preview') : 
        headers;
      setSelectedColumns(allColumns);
      setSelectAllColumns(true);
    }
  };

  const handleColumnSelect = (header) => {
    if (selectedColumns.includes(header)) {
      const newSelection = selectedColumns.filter(col => col !== header);
      setSelectedColumns(newSelection);
      // If we deselect any column, uncheck "Select All Columns"
      setSelectAllColumns(false);
    } else {
      const newSelection = [...selectedColumns, header];
      setSelectedColumns(newSelection);
      // If all columns are now selected, check "Select All Columns"
      if (newSelection.length === (isMediaFile ? 
        headers.filter(h => h !== 'preview').length : 
        headers.length)) {
        setSelectAllColumns(true);
      }
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const dataToFilter = activeConnection === 'db' ? tableData : fileData;
    if (!query) {
      setFilteredTableData(tableData);
      setFilteredFileData(fileData);
      return;
    }
    const filtered = dataToFilter.filter(item =>
      Object.values(item).some(value => {
        if (Array.isArray(value)) {
          return value.some(v => {
            if (v && typeof v === 'object' && !Array.isArray(v)) {
              return Object.values(v).some(subValue => {
                if (Array.isArray(subValue)) {
                  return subValue.some(innerValue =>
                    innerValue && String(innerValue).toLowerCase().includes(query)
                  );
                }
                return subValue && String(subValue).toLowerCase().includes(query);
              });
            }
            return v && String(v).toLowerCase().includes(query);
          });
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          return Object.values(value).some(subObject => {
            if (subObject && typeof subObject === 'object' && !Array.isArray(subObject)) {
              return Object.values(subObject).some(subValue => {
                if (Array.isArray(subValue)) {
                  return subValue.some(innerValue =>
                    innerValue && String(innerValue).toLowerCase().includes(query)
                  );
                }
                return subValue && String(subValue).toLowerCase().includes(query);
              });
            }
            return subObject && String(subObject).toLowerCase().includes(query);
          });
        }
        return value !== undefined && value !== null && String(value).toLowerCase().includes(query);
      })
    );
    if (activeConnection === 'db') {
      setFilteredTableData(filtered);
    } else {
      setFilteredFileData(filtered);
    }
    setSelectedRecords([]);
    setSelectAll(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredTableData(tableData);
    setFilteredFileData(fileData);
    setSelectedRecords([]);
    setSelectAll(false);
  };
  
  const handleSave = () => {
    if (isSaving) {
      return; // Prevent multiple saves
    }
    const dataSource = activeConnection === 'db' ? filteredTableData : filteredFileData;
    if (!dataSource || dataSource.length === 0) {
      showToast('No data available to save. Please load data first.');
      return;
    }
    
    let dataToSave;
    if (isMediaFile) {
      dataToSave = dataSource;
    } else {
      if (selectedRecords.length === 0) {
        showToast('Please select at least one record to save.');
        return;
      }
      if (selectedColumns.length === 0) {
        showToast('Please select at least one column to save.');
        return;
      }

      const columnNameMap = {};
      headers.forEach((editedName, index) => {
        const originalName = originalHeaders[index] || editedName;
        columnNameMap[originalName] = editedName;
      });

      dataToSave = dataSource
        .filter((_, index) => selectedRecords.includes(index))
        .map(row => {
          const filteredRow = {};
          selectedColumns.forEach(col => {
            const savedColumnName = columnNameMap[col] || col;
            filteredRow[savedColumnName] = row[col] !== undefined && row[col] !== null ? row[col] : '';
          });
          return filteredRow;
        });
    }
    
    if (dataToSave.length === 0) {
      showToast('No data available to save. Please ensure data is loaded.');
      return;
    }
      
    if (isMediaFile) {
      fetchAvailableBuckets(true);
      launchSaveModal(dataToSave); // Pass data directly
    } else if (fileFormat === 'document') {
      if (firestoreCollections.length === 0) {
        fetchFirestoreCollections().then((firestoreColls) => {
          if (firestoreColls.length === 0) {
            showToast('No Firestore collections available. Check permissions.');
            return;
          }
          setFirestoreCollections(firestoreColls);
          setFirestoreCollection('');
          setNewCollectionName('');
          launchSaveModal(dataToSave);
        }).catch(err => {
          console.error('Error fetching collections for save:', err);
          showToast(`Failed to fetch Firestore collections: ${err.message}`);
        });
      } else {
        setFirestoreCollection('');
        setNewCollectionName('');
        launchSaveModal(dataToSave);
      }
    } else if (fileFormat === 'bucket') {
      fetchAvailableBuckets();
      launchSaveModal(dataToSave);
    } else {
      setIsAutomationMode(false);
      launchSaveModal(dataToSave);
    }
  };

   // Fetch available buckets
  const fetchAvailableBuckets = async (keepCurrentSelection = true) => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No authenticated user found.');
      const idToken = await user.getIdToken();
     
      const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/listBuckets', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
     
      if (!response.ok) {
        throw new Error('Failed to fetch buckets');
      }
     
      const result = await response.json();
      if (result.success) {
        setAvailableBuckets(result.buckets || []);
        if (!keepCurrentSelection) {
          const testBucket = import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || 'pcakedatastoretest.firebasestorage.app';
          const isTestEnv = getCurrentUserEnv() === 'Test';
          const preferred = isTestEnv
            ? (result.buckets.find(b => b.includes('test')) || testBucket)
            : (result.buckets.find(b => b.includes('pcakedatastore') && !b.includes('test')) || result.buckets[0] || 'pcakeds_csv_backup1');
          setSelectedBucket(preferred);
        }
      }
    } catch (error) {
      console.error('Error fetching buckets:', error);
      setErrorMessage(`Failed to fetch buckets: ${error.message}`);
    }
  };
 
  // Media preview functions
  const openMediaPreview = (previewUrl, mediaType, index = 0) => {

    const mediaFilesToPreview = mediaFiles.length > 0 ? mediaFiles : [{
      previewUrl: previewUrl,
      type: mediaType,
      name: selectedFile?.name || selectedStoreFile?.split('/').pop() || 'file',
      contentType: mediaType === 'pdf' ? 'application/pdf' : 
                  mediaType === 'image' ? 'image/jpeg' : 
                  mediaType === 'video' ? 'video/mp4' : 'application/octet-stream'
    }];
    setMediaFiles(mediaFilesToPreview);
    setCurrentMediaIndex(index);
    setMediaPreviewOpen(true);
  };

  
  const navigateMedia = (direction) => {
    if (mediaFiles.length <= 1) return;
    
    let newIndex = currentMediaIndex + direction;
    if (newIndex < 0) newIndex = mediaFiles.length - 1;
    if (newIndex >= mediaFiles.length) newIndex = 0;
    
    setCurrentMediaIndex(newIndex);
  };

  const handlePrint = () => {
    // For media files, no selection is needed - automatically use the single record
    if (!isMediaFile && selectedRecords.length === 0) {
      showToast('Please select at least one record to print.');
      return;
    }
    if (!isMediaFile && selectedColumns.length === 0) {
      showToast('Please select at least one column to print.');
      return;
    }
    const dataToPrint = (activeConnection === 'db' ? filteredTableData : filteredFileData)
      .filter((_, index) => isMediaFile ? true : selectedRecords.includes(index));
    if (isMediaFile && fileType === 'image') {
      // Add index to each image for tracking
      const imagesWithIndex = dataToPrint.map((img, index) => ({
        ...img,
        index: index
      }));
     
      // Open image print preview with resize options
      openImagePrintPreview(imagesWithIndex, {
        showFileName: true,
        showSizeInfo: true,
        defaultWidth: 15,
        defaultHeight: 10
      });
    } else if (isMediaFile && (fileType === 'video' || fileType === 'document')) {
      // For non-image media files, show a message
      showToast(`Printing is not available for ${fileType} files. Please use the download or save options.`);
    } else {
      // For data files (CSV, Excel, etc.) - print as table

      const columnNameMap = {};
      headers.forEach((editedName, index) => {
        const originalName = originalHeaders[index] || editedName;
        columnNameMap[originalName] = editedName;
      });
      
      const printWindow = window.open('', '_blank');
     
      // Filter data by selected columns
      const filteredData = dataToPrint.map(row => {
        const filteredRow = {};
        selectedColumns.forEach(col => {
          filteredRow[col] = row[col] !== undefined && row[col] !== null ? String(row[col]) : '';
        });
        return filteredRow;
      });
      // Generate print HTML
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Data Print</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px 12px;
              text-align: left;
              font-size: 12px;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .print-info {
              margin-bottom: 20px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h2>Data Export - ${activeConnection === 'db' ? (selectedTable?.name || 'Table') : (selectedFile?.name || 'File')}</h2>
          <div class="print-info">
            Printed on: ${new Date().toLocaleString()}<br>
            Total records: ${filteredData.length}<br>
            Selected columns: ${selectedColumns.length}
          </div>
          <table>
            <thead>
              <tr>
                ${selectedColumns.map(header => `<th>${columnNameMap[header] || header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(row => `
                <tr>
                  ${selectedColumns.map(header => `<td>${row[header] || ''}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="print-info" style="margin-top: 20px;">
            End of report
          </div>
        </body>
        </html>
      `);
     
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const MediaPreview = ({ record, index, onPreviewClick }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Generate PDF thumbnail when component mounts
    useEffect(() => {
      if (record.type === 'pdf' && record.preview && !thumbnailUrl && !isLoading && !hasError) {
        generatePdfThumbnail(record.preview);
      }
    }, [record.type, record.preview, thumbnailUrl, isLoading, hasError]);

    const generatePdfThumbnail = (pdfUrl) => {
      setIsLoading(true);
      
      // Create an iframe to render the PDF first page
      const iframe = document.createElement('iframe');
      iframe.src = pdfUrl + '#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.visibility = 'hidden';
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        setTimeout(() => {
          try {
            // Try to capture the iframe content as an image
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            const iframeBody = iframeDoc.body;
            
            // Create a canvas to capture the thumbnail
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = 80;
            canvas.height = 60;
            
            // Fill with PDF placeholder background
            context.fillStyle = '#fff5f5';
            context.fillRect(0, 0, canvas.width, canvas.height);
            
            // Draw PDF icon as fallback
            context.fillStyle = '#e74c3c';
            context.font = 'bold 20px Arial';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText('📄', canvas.width / 2, canvas.height / 2 - 5);
            
            context.font = 'bold 8px Arial';
            context.fillText('PDF', canvas.width / 2, canvas.height / 2 + 10);
            
            const dataUrl = canvas.toDataURL('image/png');
            setThumbnailUrl(dataUrl);
            setIsLoading(false);
            
          } catch (error) {
            console.error('Error generating PDF thumbnail:', error);
            createFallbackThumbnail();
          } finally {
            document.body.removeChild(iframe);
          }
        }, 1000);
      };
      
      iframe.onerror = () => {
        console.error('Failed to load PDF for thumbnail generation');
        createFallbackThumbnail();
        document.body.removeChild(iframe);
      };
    };

    const createFallbackThumbnail = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 80;
      canvas.height = 60;
      
      // Create a nice PDF thumbnail placeholder
      context.fillStyle = '#fff5f5';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw border
      context.strokeStyle = '#e74c3c';
      context.lineWidth = 2;
      context.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);
      
      // Draw PDF icon
      context.fillStyle = '#e74c3c';
      context.font = 'bold 20px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('📄', canvas.width / 2, canvas.height / 2 - 5);
      
      // Draw "PDF" text
      context.font = 'bold 8px Arial';
      context.fillText('PDF', canvas.width / 2, canvas.height / 2 + 10);
      
      // Draw "Page 1" indicator
      context.font = 'bold 6px Arial';
      context.fillText('Page 1', canvas.width / 2, canvas.height - 8);
      
      const dataUrl = canvas.toDataURL('image/png');
      setThumbnailUrl(dataUrl);
      setHasError(true);
      setIsLoading(false);
    };

    const handleMediaClick = () => {
      if (record.preview) {
        onPreviewClick(record.preview, record.type, index);
      }
    };

    if (!record.preview) {
      return (
        <div className="table-document-preview-preview">
          <span className="hk-no-preview-text">No Preview</span>
        </div>
      );
    }

    if (record.type === 'image') {
      return (
        <img
          src={record.preview}
          alt="Preview"
          className="table-media-preview"
          onClick={handleMediaClick}
          onError={(e) => {
            console.error('Image failed to load:', record.preview);
            e.target.style.display = 'none';
          }}
          style={{ cursor: 'pointer', width: '80px', height: '60px', objectFit: 'cover' }}
        />
      );
    } else if (record.type === 'video') {
      // ... keep existing video code ...
      return (
        <div onClick={handleMediaClick} style={{ cursor: 'pointer' }}>
          {isLoading && (
            <div className="hk-media-loading" style={{
              width: '80px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              fontSize: '8px'
            }}>
              Loading...
            </div>
          )}
        
          {!isLoading && thumbnailUrl ? (
            <div
              style={{
                backgroundImage: `url(${thumbnailUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                cursor: 'pointer',
                position: 'relative',
                width: '80px',
                height: '60px',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                  background: 'rgba(250, 71, 71, 0.9)',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  ▶
                </div>
              </div>
            </div>
          ) : !isLoading ? (
            <div
              style={{
                width: '80px',
                height: '60px',
                backgroundColor: '#000',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <div style={{
                color: 'white',
                fontSize: '16px',
                textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                background: 'rgba(250, 71, 71, 0.9)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                ▶
              </div>
              <div style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                fontSize: '8px',
                color: 'white',
                background: 'rgba(0,0,0,0.7)',
                padding: '1px 3px',
                borderRadius: '2px'
              }}>
                Video
              </div>
            </div>
          ) : null}
        </div>
      );
    } else if (record.type === 'pdf') {
      return (
        <div 
          onClick={handleMediaClick} 
          style={{ 
            cursor: 'pointer',
            position: 'relative',
            width: '80px',
            height: '60px'
          }}
          title="Click to view full PDF"
        >
          {isLoading ? (
            <div className="hk-media-loading" style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              fontSize: '8px'
            }}>
              Generating thumbnail...
            </div>
          ) : thumbnailUrl ? (
            // Show PDF thumbnail
            <img
              src={thumbnailUrl}
              alt="PDF first page"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                border: '2px solid #e74c3c',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onError={createFallbackThumbnail}
            />
          ) : (
            // Fallback PDF icon
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                border: '2px solid #e74c3c',
                borderRadius: '4px',
                flexDirection: 'column',
                position: 'relative',
                padding: '5px'
              }}
            >
              <span style={{ 
                fontSize: '20px', 
                color: '#e74c3c',
                marginBottom: '2px'
              }}>
                📄
              </span>
              <span style={{ 
                fontSize: '8px', 
                color: '#e74c3c',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                PDF
              </span>
              <div style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                fontSize: '6px',
                color: '#e74c3c',
                background: 'rgba(255,255,255,0.9)',
                padding: '1px 2px',
                borderRadius: '1px'
              }}>
                Page 1
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          className="table-document-preview"
          onClick={handleMediaClick}
          style={{
            cursor: 'pointer',
            width: '80px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{ fontSize: '20px' }}>📄</span>
        </div>
      );
    }
  };

  const handleAutomate = async () => {
    if (selectedColumns.length === 0) {
      showToast('Please select at least one column to automate.');
      return;
    }

    let type, source, target;
    let maxTimestamp = null;
    
    // Helper function to parse various date formats
    const parseDateString = (dateStr) => {
      // Check for null, undefined, or empty string
      if (!dateStr || dateStr === null || dateStr === undefined || dateStr.toString().trim() === '') {
        return null;
      }
      
      // Convert to string if it's not already
      dateStr = dateStr.toString().trim();
      
      try {
        // ========== 1. TRY GERMAN FORMAT FIRST (DD.MM.YYYY HH:MM:SS) ==========
        if (dateStr.includes('.') && dateStr.includes(':') && dateStr.includes(' ')) {
          // Split by space to separate date and time
          const parts = dateStr.split(' ');
          if (parts.length === 2) {
            const datePart = parts[0];  // e.g., "08.12.2025"
            const timePart = parts[1];  // e.g., "00:14:39"
            
            // Split date by dots
            const dateParts = datePart.split('.');
            if (dateParts.length === 3) {
              // Split time by colons
              const timeParts = timePart.split(':');
              if (timeParts.length >= 2) { // Accept HH:MM or HH:MM:SS
                const day = parseInt(dateParts[0], 10);
                const month = parseInt(dateParts[1], 10) - 1; // 0-indexed
                const year = parseInt(dateParts[2], 10);
                const hour = parseInt(timeParts[0], 10);
                const minute = parseInt(timeParts[1], 10);
                const second = timeParts.length === 3 ? parseInt(timeParts[2], 10) : 0;
                
                // Validate all parts are numbers
                if (!isNaN(day) && !isNaN(month) && !isNaN(year) && 
                    !isNaN(hour) && !isNaN(minute) && !isNaN(second)) {
                  
                  const date = new Date(year, month, day, hour, minute, second);
                  
                  if (!isNaN(date.getTime())) {
                    return date;
                  }
                }
              }
            }
          }
        }
        
        // ========== 2. TRY DB COLLECTION FORMAT ==========
        const dbFormatRegex = /(\d{1,2}) (\w{3,}) (\d{4}) at (\d{1,2}):(\d{2}):(\d{2}) (?:UTC)?([+-]\d{1,2})?/i;
        const dbMatch = dateStr.match(dbFormatRegex);
        
        if (dbMatch) {
          const [, day, monthStr, year, hour, minute, second, timezone] = dbMatch;
          
          // Map month names to numbers
          const monthNames = {
            'jan': 0, 'january': 0,
            'feb': 1, 'february': 1,
            'mar': 2, 'march': 2,
            'apr': 3, 'april': 3,
            'may': 4,
            'jun': 5, 'june': 5,
            'jul': 6, 'july': 6,
            'aug': 7, 'august': 7,
            'sep': 8, 'september': 8,
            'oct': 9, 'october': 9,
            'nov': 10, 'november': 10,
            'dec': 11, 'december': 11
          };
          
          const month = monthNames[monthStr.toLowerCase()];
          if (month !== undefined) {
            // Create date in local timezone
            const date = new Date(
              parseInt(year),
              month,
              parseInt(day),
              parseInt(hour),
              parseInt(minute),
              parseInt(second)
            );
            
            // Adjust for timezone if specified
            if (timezone) {
              const tzOffset = parseInt(timezone) * 60 * 60 * 1000;
              date.setTime(date.getTime() - tzOffset);
            }
            
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
        }
        
        // ========== 3. TRY ISO FORMAT (Google Forms) ==========
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) {
          // Additional check: if it's an ISO string (contains T and Z or timezone)
          if (dateStr.includes('T') && (dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-'))) {
            return isoDate;
          }
        }
        
        // ========== 4. TRY AS SIMPLE DATE STRING ==========
        const simpleDate = new Date(dateStr.replace(/ at /g, ' '));
        if (!isNaN(simpleDate.getTime())) {
          return simpleDate;
        }
        
      } catch (e) {
        console.warn('Failed to parse date:', dateStr, e);
      }
      return null;
    };
    
    // Determine connection type
    if (activeConnection === 'db') {
      type = 'DB';
      // Determine source for Firestore
      if (selectedTable?.name === 'otemplate' && selectedOtemplateDoc) {
        source = `otemplate_${selectedOtemplateDoc}`;
      } else {
        source = selectedTable?.name || '';
      }
      // Target for Firestore is the filename
      target = `${source}`;
      
      // For DB tables, find max of updated or created timestamps ONLY
      const dataSource = filteredTableData;
      if (dataSource && dataSource.length > 0) {
        let maxDate = null;
        
        dataSource.forEach(record => {
          let recordDate = null;
          let updatedDate = null;
          let createdDate = null;
          
          // Check for updated field ONLY - handle null properly
          if (record.updated !== null && record.updated !== undefined && record.updated !== '') {
            updatedDate = parseDateString(record.updated);
          }
          
          // Check for created field ONLY - handle null properly
          if (record.created !== null && record.created !== undefined && record.created !== '') {
            createdDate = parseDateString(record.created);
          }
          
          // Take the higher of updated or created for this record (if both exist)
          if (updatedDate && createdDate) {
            recordDate = updatedDate > createdDate ? updatedDate : createdDate;
          } else if (updatedDate) {
            recordDate = updatedDate;
          } else if (createdDate) {
            recordDate = createdDate;
          }
          // If neither updated nor created exists, recordDate remains null
          
          // Update max date if found
          if (recordDate && (!maxDate || recordDate > maxDate)) {
            maxDate = recordDate;
          }
        });
        
        if (maxDate) {
          maxTimestamp = maxDate.toISOString();
        }
      }
      
    } else if (activeConnection === 'drive') {
      type = 'Drive';
      
      // Determine source and target based on whether it's a form or sheet
      if (selectedForm) {
        source = selectedForm.name;
        target = 'FormOrders';
        
        // For Google Forms, find max lastSubmittedTime
        const dataSource = formResponseData || filteredFileData;
        if (dataSource && dataSource.length > 0) {
          let maxDate = null;
          
          dataSource.forEach(record => {
            if (record.lastSubmittedTime !== null && record.lastSubmittedTime !== undefined && record.lastSubmittedTime !== '') {
              try {
                const date = parseDateString(record.lastSubmittedTime);
                if (date && !isNaN(date.getTime()) && (!maxDate || date > maxDate)) {
                  maxDate = date;
                }
              } catch (e) {
                console.warn('Failed to parse lastSubmittedTime:', record.lastSubmittedTime, e);
              }
            } else if (record.createTime !== null && record.createTime !== undefined && record.createTime !== '') {
              // Fallback to createTime if lastSubmittedTime not available
              try {
                const date = parseDateString(record.createTime);
                if (date && !isNaN(date.getTime()) && (!maxDate || date > maxDate)) {
                  maxDate = date;
                }
              } catch (e) {
                console.warn('Failed to parse createTime:', record.createTime, e);
              }
            }
          });
          
          if (maxDate) {
            maxTimestamp = maxDate.toISOString();
          }
        }
        
      } else if (selectedSheet) {
        source = selectedSheet.name;
        target = 'SheetOrders';
        
        // For Google Sheets, find max of Zeitstempel or Timestamp columns
        const dataSource = filteredFileData;
        if (dataSource && dataSource.length > 0) {
          let maxDate = null;
          let validDatesFound = false;
          
          // Look for timestamp columns (case-insensitive)
          const timestampColumns = Object.keys(dataSource[0] || {}).filter(key => 
            key.toLowerCase().includes('zeitstempel') || 
            key.toLowerCase().includes('timestamp')
          );
          
          if (timestampColumns.length > 0) {
            dataSource.forEach((record, index) => {
              timestampColumns.forEach(column => {
                const timestampStr = record[column];
                
                // Check for null, undefined, or empty string
                if (timestampStr !== null && timestampStr !== undefined && timestampStr !== '') {                  
                  const date = parseDateString(timestampStr);
                  if (date && !isNaN(date.getTime())) {
                    validDatesFound = true;                    
                    if (!maxDate || date > maxDate) {
                      maxDate = date;
                    }
                  }
                } 
              });
            });
          }
          
          // Only set maxTimestamp if we found at least one valid date
          if (validDatesFound && maxDate) {
            maxTimestamp = maxDate.toISOString();
          } 
        }
      } else {
        source = '';
        target = '';
      }
    } else {
      showToast('Automation not supported for this connection type.');
      return;
    }

    // Build reverse mapping: from current header name to its original name
    const currentToOriginalMap = {};
    
    // First, map from originalHeaders to current headers
    originalHeaders.forEach((originalName, index) => {
      if (index < headers.length) {
        const currentName = headers[index];
        currentToOriginalMap[currentName] = originalName;
      }
    });
    
    // Then, apply any header renames from headerMapping
    Object.entries(headerMapping).forEach(([oldName, newName]) => {
      // Find what original name was mapped to oldName
      const originalOfOld = Object.entries(currentToOriginalMap).find(
        ([current, original]) => current === oldName
      )?.[1] || oldName;
      
      // Update mapping for newName to point to the original of oldName
      currentToOriginalMap[newName] = originalOfOld;
      
      // Remove oldName from mapping if it still exists
      if (currentToOriginalMap[oldName]) {
        delete currentToOriginalMap[oldName];
      }
    });

    // Build columns information according to new logic
    const selectedOriginalNames = [];
    const changedColumns = [];
    
    selectedColumns.forEach(column => {
      const originalName = currentToOriginalMap[column] || column;
      selectedOriginalNames.push(originalName);
      
      if (originalName !== column) {
        changedColumns.push(`${originalName}>${column}`);
      }
    });

    // Determine if all columns are selected
    const allColumnsSelected = selectedColumns.length === headers.length;
    
    // Build the columns string
    let columnsField = '';
    if (allColumnsSelected) {
      columnsField = 'All';
    } else {
      columnsField = [...new Set(selectedOriginalNames)].join(',');
    }
    
    const changedColumnsField = changedColumns.join(',');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const metaFilePath = `meta/${source.replace(/[^a-zA-Z0-9]/g, '_')}_meta_${timestamp}.csv`;
    
    // Format the max timestamp for display (only if we found one)
    let maxTimestampFormatted = '';
    if (maxTimestamp) {
      try {
        const date = new Date(maxTimestamp);
        if (!isNaN(date.getTime())) {
          // Format: 18/11/2025 at 15:38:32 (en-GB format)
          maxTimestampFormatted = date.toLocaleString('en-GB', { 
            timeZone: 'Europe/Paris', 
            hour12: false,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).replace(', ', ' at ');
        }
      } catch (e) {
        console.error('Error formatting max timestamp:', e);
      }
    }
    
    // Prepare meta data with new columns format
    const metaHeaders = ['Type', 'Source', 'Target', 'Columns', 'Changed Columns', 'Max Updated/Created', 'File Creation Timestamp', 'Frequency'];
    const metaData = [
      type,
      source,
      target,
      columnsField,
      changedColumnsField,
      maxTimestampFormatted, // Will be blank if no timestamp found
      new Date().toLocaleString('en-GB', { timeZone: 'Europe/Paris', hour12: false }).replace(', ', ' at '),
      'daily',
    ];

    const metaContent = Papa.unparse([metaHeaders, metaData], { 
      quotes: true, 
      quoteChar: '"', 
      delimiter: ',' 
    });

    try {
      const metaBlob = new Blob([metaContent], { type: 'text/csv' });
      await uploadToStorage(metaFilePath, metaBlob, 'text/csv', defaultBucket);
      showToast('Meta file successfully saved to Cloud Storage meta folder!');
    } catch (error) {
      console.error('Error saving meta file:', error.message, error.stack);
      showToast(`Failed to save meta file: ${error.message}`);
    }
  };

  const handleDelete = () => {
    if (activeConnection === 'drive') {
      if (selectedSheet) {
        if (selectedRecords.length > 0) {
          // Delete specific rows from sheet
          const rowNumbers = selectedRecords.map(index => index + 2); // +2 for header row and 0-index
          setDeleteTarget(`${selectedRecords.length} row(s) from "${selectedSheet.name}"`);
          setDeleteContext({
            type: 'drive',
            subtype: 'sheet-rows',
            sheetId: selectedSheet.id,
            sheetName: selectedTab || selectedSheet.name,
            rowNumbers: rowNumbers,
            displayName: `${selectedSheet.name} - ${selectedTab || 'Sheet'}`
          });
          setIsDeleteModalOpen(true);
        } else {
          // Delete entire sheet
          showToast('Please select at least one record to Delete');
          return;
        }
      } else if (selectedForm) {
        if (selectedRecords.length > 0) {
          // Delete specific form responses
          const selectedResponses = selectedRecords.map(index => filteredFileData[index]);
          setDeleteTarget(`${selectedResponses.length} form response(s) from "${selectedForm.name}"`);
          setDeleteItems(selectedResponses.map(r => ({
            id: r.lastSubmittedTime || r.createTime, // Use lastSubmittedTime first, fallback to createTime
            responseId: r.responseId, // Forms API responseId
            createTime: r.createTime,
            lastSubmittedTime: r.lastSubmittedTime, // This is what Apps Script needs
            displayName: `Response from ${r.respondentEmail || r.lastSubmittedTime?.substring(0, 10) || r.createTime?.substring(0, 10) || 'Unknown'}`
          })));
          setDeleteContext({
            type: 'drive',
            subtype: 'form-responses',
            formId: selectedForm.id,
            displayName: selectedForm.name
          });
          setIsDeleteModalOpen(true);
        } else {
          showToast('Please select at least one record to Delete');
          return;
        }
      } else {
        showToast('Please select a Google Sheet or Form to delete.');
      }
      return;
    }
    if (activeConnection === 'store') {
      if (selectedStoreFile && selectedRecords.length > 0) {
        // Rows selected within an open CSV — delete those rows from the file
        const selectedRows = selectedRecords.map(index => filteredFileData[index]).filter(Boolean);
        if (selectedRows.length === 0) {
          setErrorMessage('No valid rows selected for deletion.');
          return;
        }
        const fileName = selectedStoreFile.split('/').pop();
        setDeleteTarget(`${selectedRows.length} row(s) from "${fileName}"`);
        setDeleteItems(selectedRows);
        setDeleteContext({
          type: 'store',
          subtype: 'csv-rows',
          fileName: selectedStoreFile,
          bucketName: selectedBucket,
          displayName: fileName
        });
        setIsDeleteModalOpen(true);
      } else if (selectedStoreFile) {
        setDeleteTarget(`file "${selectedStoreFile.split('/').pop()}"`);
        setDeleteItems([{ 
          name: selectedStoreFile, 
          displayName: selectedStoreFile.split('/').pop() 
        }]);
        setDeleteContext({
          type: 'store',
          folderPath: currentPath,
          bucketName: selectedBucket,
          displayName: currentPath || 'current folder'
        });
        setIsDeleteModalOpen(true);
      } else if (selectedRecords.length === 1) {
        const fileName = filteredFileData[selectedRecords[0]]?.name;
        if (fileName) {
          setDeleteTarget(`file "${fileName.split('/').pop()}"`);
          setDeleteItems([{ 
            name: fileName, 
            displayName: fileName.split('/').pop() 
          }]);
          setDeleteContext({
            type: 'store',
            folderPath: currentPath,
            bucketName: selectedBucket,
            displayName: currentPath || 'current folder'
          });
          setIsDeleteModalOpen(true);
        } else {
          setErrorMessage('No valid file selected for deletion.');
        }
      } else if (selectedRecords.length > 1) {
        const selectedFiles = selectedRecords.map(index => {
          const file = filteredFileData[index];
          return {
            name: file?.name,
            displayName: file?.name?.split('/').pop()
          };
        }).filter(file => file.name);
        
        if (selectedFiles.length > 0) {
          setDeleteTarget(`${selectedFiles.length} files`);
          setDeleteItems(selectedFiles);
          setDeleteContext({
            type: 'store',
            folderPath: currentPath,
            bucketName: selectedBucket,
            displayName: currentPath || 'current folder'
          });
          setIsDeleteModalOpen(true);
        } else {
          setErrorMessage('No valid files selected for deletion.');
        }
      } else {
        setErrorMessage('Please select a file to delete.');
        return;
      }
    } else if (activeConnection === 'db' && selectedTable) {
      if (selectedTable.name === 'otemplate' && selectedOtemplateDoc) {
        setDeleteTarget(`otemplate document "${selectedOtemplateDoc}"`);
        setDeleteContext({
          type: 'db',
          collectionName: 'otemplate',
          displayName: 'otemplate collection'
        });
        setIsDeleteModalOpen(true);
      } else if (selectedRecords.length > 0) {
        // Delete only the selected rows (Firestore documents)
        const selectedDocs = selectedRecords
          .map(index => filteredTableData[index])
          .filter(d => d?.id);
        if (selectedDocs.length === 0) {
          setErrorMessage('Selected records have no valid document IDs.');
          return;
        }
        setDeleteTarget(`${selectedDocs.length} record(s) from "${selectedTable.name}"`);
        setDeleteItems(selectedDocs.map(d => ({ id: d.id, displayName: d.id })));
        setDeleteContext({
          type: 'db',
          subtype: 'documents',
          collectionName: selectedTable.name,
          displayName: selectedTable.name
        });
        setIsDeleteModalOpen(true);
      } else {
        // No records selected → confirm deletion of entire collection
        setDeleteTarget(`collection "${selectedTable.name}"`);
        setDeleteContext({
          type: 'db',
          subtype: 'collection',
          collectionName: selectedTable.name,
          displayName: selectedTable.name
        });
        setIsDeleteModalOpen(true);
      }
    } else {
      setErrorMessage('No table or file selected for deletion.');
      return;
    }
  };

  const confirmMultiDelete = async (selectedItemIds) => {
    setDeleting(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No authenticated user found.');
      const idToken = await user.getIdToken();

      let successfulDeletes = [];
      let failedDeletes = [];

      if (deleteContext.type === 'store') {
        const deletePromises = selectedItemIds.map(fileName =>
          deleteFromStorage(fileName, deleteContext.bucketName)
        );

        const results = await Promise.allSettled(deletePromises);
        
        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          if (result.status === 'fulfilled' && result.value?.success) {
            successfulDeletes.push(selectedItemIds[i]);
          } else {
            failedDeletes.push({
              item: selectedItemIds[i],
              error: result.reason?.message || result.value?.error?.message || 'Unknown error'
            });
          }
        }

      } else if (deleteContext.type === 'db') {
        // selectedItemIds are now collection names — delete each entire collection
        for (const collectionName of selectedItemIds) {
          try {
            // Fetch all doc IDs in this collection
            const docIds = await fetchCollectionDocIds(collectionName);

            // Delete every document
            let allDocsDeleted = true;
            for (const docId of docIds) {
              const response = await fetch(getDeleteDocEndpoint(), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({ collectionName, documentId: docId }),
              });
              const data = await response.json();
              if (!response.ok || !data.success) {
                console.warn(`Failed to delete doc ${docId} in ${collectionName}:`, data.error?.message);
                allDocsDeleted = false;
              }
            }

            // Clean up the now-empty collection
            try {
              await fetch(getDeleteEmptyColEndpoint(), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({ collectionName }),
              });
            } catch (e) {
              console.warn('deleteEmptyCollection warning for', collectionName, e);
            }

            if (allDocsDeleted) {
              successfulDeletes.push(collectionName);
            } else {
              failedDeletes.push({ item: collectionName, error: 'Some documents could not be deleted' });
            }
          } catch (colErr) {
            failedDeletes.push({ item: collectionName, error: colErr.message || 'Unknown error' });
          }
        }
      }

      if (successfulDeletes.length > 0) {
        const successMessage = `Successfully deleted ${successfulDeletes.length} ${deleteContext.type === 'store' ? 'file(s)' : 'collection(s)'}.`;
        showToast(successMessage);
        
        if (deleteContext.type === 'store') {
          fetchStoreFiles(currentPath, selectedBucket);
        } else if (deleteContext.type === 'db') {
          if (selectedTable && successfulDeletes.includes(selectedTable.name)) {
            setSelectedTable(null);
            setTableData([]);
            setFilteredTableData([]);
          }
          fetchFirestoreCollections().then((firestoreColls) => {
            setFirestoreCollections(firestoreColls);
          });
        }
      
        setSelectedRecords([]);
        setSelectAll(false);
      }

      if (failedDeletes.length > 0) {
        const errorDetails = failedDeletes.map(f => `• ${f.item}: ${f.error}`).join('\n');
        const errorMessage = `Failed to delete ${failedDeletes.length} ${deleteContext.type === 'store' ? 'file(s)' : 'collection(s)'}:\n${errorDetails}`;
        setErrorMessage(errorMessage);
      }

    } catch (error) {
      console.error('Multi-deletion error:', error);
      setErrorMessage(`Deletion failed: ${error.message}`);
    } finally {
      setDeleting(false);
      setIsDeleteModalOpen(false);
      setDeleteContext(null);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No authenticated user found.');
      const idToken = await user.getIdToken();

      // Handle Google Drive deletions
      if (activeConnection === 'drive') {
        if (selectedSheet && deleteContext?.subtype === 'sheet') {
          // Delete entire Google Sheet
          const success = await deleteGoogleSheet(selectedSheet.id);
          if (success) {
            showToast(`Google Sheet "${selectedSheet.name}" deleted successfully.`);
            
            // Refresh sheets list
            const sheetsResult = await fetchGoogleSheets();
            if (sheetsResult.success) {
              setGoogleSheets(sheetsResult.sheets || []);
            }
            
            // Clear selection
            setSelectedSheet(null);
            setSheetTabs([]);
            setSelectedTab(null);
          }
        } 
        else if (selectedSheet && deleteContext?.subtype === 'sheet-rows') {
          // Delete specific rows from sheet
          const { rowNumbers } = deleteContext;
          await deleteSheetRows(selectedSheet.id, selectedTab, rowNumbers);
          showToast(`${rowNumbers.length} row(s) deleted successfully.`);
          
          // Refresh sheet data
          if (selectedTab) {
            await handleTabSelectAndImport(selectedTab);
          }
        }
        else if (selectedForm && deleteContext?.subtype === 'form-responses') {
          // Delete specific form responses using context function directly
          const timestamps = deleteItems.map(item => item.id);           
          try {
            const results = [];
            
            for (let i = 0; i < timestamps.length; i++) {
              const timestamp = timestamps[i];
              const deleteItem = deleteItems[i];
              try {
                // Pass an object with all timestamps so the backend can decide which to use
                const result = await deleteFormResponse(selectedForm.id, {
                  timestamp: timestamp,
                  lastSubmittedTime: deleteItem.lastSubmittedTime,
                  createTime: deleteItem.createTime
                });
                
                results.push({
                  timestamp: timestamp,
                  success: result.success,
                  message: result.message || 'Deleted',
                  usedTimestamp: result.details?.matchMethod || 'unknown'
                });
                
                // Update state after each successful deletion
                // Filter by lastSubmittedTime if available, otherwise by createTime
                if (result.success) {
                  setFilteredFileData(prev => prev.filter(item => 
                    !(item.lastSubmittedTime === deleteItem.lastSubmittedTime && item.createTime === deleteItem.createTime)
                  ));
                  setFormResponseData(prev => prev.filter(item => 
                    !(item.lastSubmittedTime === deleteItem.lastSubmittedTime && item.createTime === deleteItem.createTime)
                  ));
                  setFileData(prev => prev.filter(item => 
                    !(item.lastSubmittedTime === deleteItem.lastSubmittedTime && item.createTime === deleteItem.createTime)
                  ));
                }
                
                // Small delay between requests
                if (i < timestamps.length - 1) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
                
              } catch (error) {
                console.error(`Failed to delete response ${timestamp}:`, error);
                results.push({
                  timestamp: timestamp,
                  success: false,
                  error: error.message
                });
                continue;
              }
            }            
            // Update selections after deletion
            setSelectedRecords([]);
            setSelectAll(false);
            
            // Show success/failure message
            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;
            
            if (successful > 0) {
              const successMessage = successful === 1 
                ? 'Form response deleted successfully.'
                : `${successful} form responses deleted successfully${failed > 0 ? `, ${failed} failed` : ''}.`;
              showToast(successMessage);
              
              // If all responses were deleted, refresh the form data
              if (filteredFileData.length === timestamps.length) {
                try {
                  await handleFormSelect(selectedForm.id);
                } catch (refreshError) {
                  console.error('Error refreshing form data:', refreshError);
                  setErrorMessage(`Deleted successfully but failed to refresh: ${refreshError.message}`);
                }
              }
            }
            
            if (failed > 0) {
              const errorMessages = results
                .filter(r => !r.success)
                .map(r => `• Response (timestamp: ${r.timestamp?.substring(0, 19)}): ${r.error || 'Unknown error'}`)
                .join('\n');
              
              setErrorMessage(`Failed to delete ${failed} response(s):\n${errorMessages}`);
            }
            
            // Handle case where all deletions failed
            if (successful === 0 && failed > 0) {
              setErrorMessage(`All ${failed} deletion attempts failed. No responses were deleted.`);
            }
            
            // Clear delete items and context regardless of outcome
            setDeleteItems([]);
            setDeleteContext(null);
            
          } catch (error) {
            console.error('Deletion process error:', error);
            
            // Reset states on error
            setSelectedRecords([]);
            setSelectAll(false);
            setDeleteItems([]);
            setDeleteContext(null);
            
            // Check for specific error types
            if (error.message.includes('insufficient authentication scopes') || 
                error.message.includes('Request had insufficient authentication scopes') ||
                error.message.includes('Permission denied') ||
                error.message.includes('403')) {
              
              setErrorMessage(
                'Google Drive permissions are insufficient for deletion. ' +
                'Please reconnect with updated permissions to delete form responses.'
              );
              setShowReconnectButton(true);
              
            } else if (error.message.includes('404') || error.message.includes('Not Found')) {
              setErrorMessage(
                'Form or response not found. It may have been already deleted or you no longer have access.'
              );
            } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
              setErrorMessage(
                'Your Google Drive session has expired. Please reconnect to Google Drive.'
              );
              setShowReconnectButton(true);
            } else if (error.message.includes('Apps Script') || error.message.includes('script.google.com')) {
              setErrorMessage(
                'Apps Script deletion service failed. Please check if the Apps Script is properly deployed and accessible.'
              );
            } else {
              setErrorMessage(`Deletion failed: ${error.message}`);
            }
          }
          
          // Close the modal
          setIsDeleteModalOpen(false);
        }
      } 
      // Existing store deletion logic
      else if (activeConnection === 'store' && deleteItems.length > 0) {
        if (deleteContext?.subtype === 'csv-rows') {
          // Remove selected rows from the CSV and overwrite the file in the bucket
          const deleteSet = new Set(deleteItems.map(r => JSON.stringify(r)));
          const remainingRows = fileData.filter(r => !deleteSet.has(JSON.stringify(r)));
          const headers = originalHeaders.length > 0 ? originalHeaders : Object.keys(fileData[0] || {});
          const csvContent = Papa.unparse([headers, ...remainingRows.map(r => headers.map(h => r[h] ?? ''))]);
          const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/writeFile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ fileName: deleteContext.fileName, content: csvContent }),
          });
          const result = await response.json();
          if (!response.ok || !result.success) {
            throw new Error(result.error?.message || 'Failed to save updated CSV');
          }
          showToast(`${deleteItems.length} row(s) deleted from "${deleteContext.displayName}".`);
          setFileData(remainingRows);
          setFilteredFileData(remainingRows);
          setSelectedRecords([]);
          setSelectAll(false);
        } else {
          let successCount = 0;
          let failedNames = [];

          for (const item of deleteItems) {
            try {
              const result = await deleteFromStorage(item.name, selectedBucket);
              if (!result?.success) {
                failedNames.push(item.displayName || item.name);
              } else {
                successCount++;
              }
            } catch (fileErr) {
              failedNames.push(item.displayName || item.name);
            }
          }

          if (successCount > 0) {
            const msg = successCount === 1
              ? `File "${deleteItems[0].displayName}" deleted successfully.`
              : `${successCount} file(s) deleted successfully.${failedNames.length > 0 ? ` ${failedNames.length} failed.` : ''}`;
            showToast(msg);
          }
          if (failedNames.length > 0 && successCount === 0) {
            throw new Error(`Failed to delete: ${failedNames.join(', ')}`);
          }

          setSelectedRecords([]);
          setSelectAll(false);
          setSelectedStoreFile('');
          setFileData([]);
          setFilteredFileData([]);
          fetchStoreFiles(currentPath, selectedBucket);
        }
      } 
      // Existing Firestore deletion logic
      else if (activeConnection === 'db' && selectedTable) {
        if (selectedTable.name === 'otemplate' && selectedOtemplateDoc) {
          const response = await fetch(getDeleteDocEndpoint(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ 
              collectionName: 'otemplate', 
              documentId: selectedOtemplateDoc 
            }),
          });
          
          const result = await response.json();
          if (!response.ok) {
            throw new Error(result.error?.message || 'Failed to delete document');
          }
          
          if (result.success) {
            showToast(`Document ${selectedOtemplateDoc} deleted successfully from otemplate.`);
            setSelectedOtemplateDoc(null);
            setTableData([]);
            setFilteredTableData([]);
          }
        } else if (deleteContext?.subtype === 'documents') {
          // Delete only the selected documents
          let successCount = 0;
          let failedIds = [];
          const deletedIds = new Set();

          for (const item of deleteItems) {
            const response = await fetch(getDeleteDocEndpoint(), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              body: JSON.stringify({ 
                collectionName: selectedTable.name, 
                documentId: item.id 
              }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
              failedIds.push(item.id);
              console.warn(`Failed to delete document ${item.id}: ${result.error?.message}`);
            } else {
              successCount++;
              deletedIds.add(item.id);
            }
          }

          if (successCount > 0) {
            showToast(`${successCount} record(s) deleted from "${selectedTable.name}".${failedIds.length > 0 ? ` ${failedIds.length} failed.` : ''}`);
            // Remove deleted rows from local state immediately
            setTableData(prev => prev.filter(r => !deletedIds.has(r.id)));
            setFilteredTableData(prev => prev.filter(r => !deletedIds.has(r.id)));
            setSelectedRecords([]);
            setSelectAll(false);
          }
          if (failedIds.length > 0 && successCount === 0) {
            throw new Error(`Failed to delete the selected records.`);
          }
        } else {
          // No records selected — delete entire collection
          const docIds = await fetchCollectionDocIds(selectedTable.name);
          
          for (const docId of docIds) {
            const response = await fetch(getDeleteDocEndpoint(), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              body: JSON.stringify({ 
                collectionName: selectedTable.name, 
                documentId: docId 
              }),
            });
            
            const result = await response.json();
            if (!response.ok) {
              console.warn(`Failed to delete document ${docId}: ${result.error?.message}`);
            }
          }

          showToast(`All documents in ${selectedTable.name} deleted successfully.`);
          
          const emptyCollectionResponse = await fetch(getDeleteEmptyColEndpoint(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ collectionName: selectedTable.name }),
          });
          
          const emptyCollectionResult = await emptyCollectionResponse.json();

          setSelectedTable(null);
          setTableData([]);
          setFilteredTableData([]);
        }
      }
      
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      setDeleteItems([]);
      
    } catch (error) {
      console.error('Deletion error:', error);
      // Check for insufficient scopes error
      if (error.message.includes('insufficient authentication scopes') || 
          error.message.includes('Request had insufficient authentication scopes')) {
        
        setErrorMessage(
          'Google Drive permissions are outdated. ' +
          'You need to reconnect with new permissions to delete items.'
        );
        
        // You can auto-trigger reconnection or show a button
        // Option 1: Auto-reconnect (more aggressive)
        // await handleDriveReconnect();
        
        // Option 2: Show reconnect button (user-friendly)
        setShowReconnectButton(true);
        
      } else {
        setErrorMessage(`Deletion failed: ${error.message}`);
      }
    } finally {
      setDeleting(false);
    }
  };


  useEffect(() => {
    return () => {
      mediaFiles.forEach(mediaFile => {
        if (mediaFile.previewUrl && mediaFile.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(mediaFile.previewUrl);
        }
      });
      if (mediaPreviewUrl && mediaPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(mediaPreviewUrl);
      }
    };
  }, [mediaFiles, mediaPreviewUrl]);

  useEffect(() => {
    if (showConnectedMessage) {
      const timer = setTimeout(() => {
        setShowConnectedMessage(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showConnectedMessage]);
  const shouldShowDriveOption = hasDriveAccess() && getCurrentUserEnv() !== 'Test';

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleFileUpload} 
      />
      <div className="hk-outer-page">
      <div className={`housekeeping-page ${isMediaFile ? 'media-file-mode' : ''}`}>
        <button className="hk-close-btn" onClick={handleClose} title="Close">
          <FaTimes />
        </button>
        <div className="housekeeping-filter-section">
          <input
            type="text"
            className="housekeeping-filter-input"
            placeholder="Search across all fields..."
            value={searchQuery}
            onChange={handleSearchChange} />
          {searchQuery && (
            <button className="housekeeping-search-clear-btn" onClick={handleClearSearch}>
              <FaTimes />
            </button>
          )}
        </div>
       
        <div className="housekeeping-connect-container">
          <div className="connection-header">
            <div className="connection-left">
              <div className="connection-buttons-group">
                <div style={{ position: 'relative' }} ref={connectMenuRef}>
                  <button
                    className="housekeeping-plus-btn"
                    onClick={() => setIsConnectMenuOpen(!isConnectMenuOpen)}
                  >
                    <FaPlus style={{ fontSize: '16px' }} />
                  </button>
                  {isConnectMenuOpen && (
                    <div className="housekeeping-dropdown-menu" style={{ left: 0, right: 'auto' }}>
                      <button
                        className="housekeeping-dropdown-item"
                        onClick={() => handleConnectOption('db')}
                      >
                        Connect DB
                      </button>
                      <button
                        className="housekeeping-dropdown-item"
                        onClick={() => handleConnectOption('file')}
                      >
                        Connect File
                      </button>
                      {shouldShowDriveOption && (
                        <button
                          className="housekeeping-dropdown-item"
                          onClick={() => handleConnectOption('drive')}
                          disabled={driveLoading}
                          style={{
                            opacity: driveLoading ? 0.6 : 1,
                            cursor: driveLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          {driveLoading ? (
                            <>
                              <span>🔄</span> Connecting...
                            </>
                          ) : isDriveConnected ? (
                            <>
                              <span>✅</span> Select Sheet/Form
                            </>
                          ) : (
                            <>
                              Connect Drive
                            </>
                          )}
                        </button>
                      )}
                      <button
                        className="housekeeping-dropdown-item"
                        onClick={() => handleConnectOption('store')}
                      >
                        Connect Bucket
                      </button>
                    </div>
                  )}
                </div>
                {activeConnection && (
                  <FaSync
                    className="housekeeping-icon-large"
                    onClick={() => handleConnectOption(activeConnection)}
                    title="Refresh"
                    style={{ fontSize: '20px', cursor: 'pointer', marginLeft: '25px' }} />
                )}
              </div>
            </div>
            <div className="connection-right">
              {activeConnection && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {(activeConnection === 'store' || activeConnection === 'drive') && selectedRecords.length > 0 && (
                    <span className="hk-selected-count-badge">
                      {selectedRecords.length} {activeConnection === 'store' ? (selectedStoreFile ? 'row' : 'file') : 'item'}{selectedRecords.length > 1 ? 's' : ''} selected
                      {activeConnection === 'drive' && selectedSheet && ' (rows)'}
                      {activeConnection === 'drive' && selectedForm && ' (responses)'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="connection-selectors">
            <div className="housekeeping-select-container">
              {activeConnection === 'db' && collections.length > 0 && (
                <select
                  onChange={(e) => handleTableSelect(e.target.value)}
                  className="housekeeping-select"
                  value={selectedTable?.name || ''}
                  style={{ minWidth: '200px' }}
                >
                  <option value="" disabled>Select a table</option>
                  {collections.map((coll) => (
                    <option key={coll.name} value={coll.name}>
                      {coll.name}
                    </option>
                  ))}
                </select>
              )}
              {selectedTable?.name === 'otemplate' && otemplateDocs.length > 0 && (
                <select
                  onChange={(e) => handleOtemplateDocSelect(e.target.value)}
                  className="housekeeping-select"
                  value={selectedOtemplateDoc || ''}
                >
                  <option value="" disabled>Select a document</option>
                  {otemplateDocs.map((docId) => (
                    <option key={docId} value={docId}>
                      {docId}
                    </option>
                  ))}
                </select>
              )}
              {activeConnection === 'drive' && googleSheets.length > 0 && (
                <select
                  onChange={(e) => handleSheetSelect(e.target.value)}
                  className="housekeeping-select"
                  value={selectedSheet?.id || ''}
                >
                  <option value="" disabled>Select a Google Sheet</option>
                  {googleSheets.map(sheet => (
                    <option key={sheet.id} value={sheet.id}>
                      {sheet.name}
                    </option>
                  ))}
                </select>
              )}
              {selectedSheet && sheetTabs.length > 0 && (
                <select
                  onChange={(e) => {
                    setSelectedTab(e.target.value);
                    handleTabSelectAndImport(e.target.value);
                  } }
                  className="housekeeping-select"
                  value={selectedTab || ''}
                >
                  <option value="" disabled>Select a tab</option>
                  {sheetTabs.map(tab => (
                    <option key={tab} value={tab}>
                      {tab}
                    </option>
                  ))}
                </select>
              )}
              {activeConnection === 'drive' && googleForms.length > 0 && (
                <select
                  onChange={(e) => handleFormSelect(e.target.value)}
                  className="housekeeping-select"
                  value={selectedForm?.id || ''}
                >
                  <option value="" disabled>Select a Google Form</option>
                  {googleForms.map(form => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                    </option>
                  ))}
                </select>
              )}
              {activeConnection === 'store' && (
                <div className="store-connection-section">
                  <div className="bucket-selector-row">
                    <select
                      onChange={(e) => {
                        const newBucket = e.target.value;
                        setSelectedBucket(newBucket);
                        initializeStoreConnection(newBucket);
                      } }
                      className="housekeeping-select"
                      value={selectedBucket || ''}
                      style={{ minWidth: '250px' }}
                    >
                      {getCurrentUserEnv() === 'Test' ? (
                        <option value={import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || 'pcakedatastoretest.firebasestorage.app'}>
                          {import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || 'pcakedatastoretest.firebasestorage.app'}
                        </option>
                      ) : (
                        <>
                          <option value="pcakeds_csv_backup1">pcakeds_csv_backup1</option>
                          <option value="pcakedatastore.appspot.com">pcakedatastore.appspot.com</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div className="folder-navigation">
                    <div
                      className="hk-folder-item hk-root-folder"
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        margin: '4px 0',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: '500',
                        border: '2px solid transparent'
                      }}
                      data-selected={currentPath === '' ? 'true' : undefined}
                      onClick={() => fetchStoreFiles('')}
                    >
                      <span style={{ marginRight: '8px', fontSize: '16px' }}>📁</span>
                      Root Folder {currentPath && `/ ${currentPath}`}
                    </div>
                    <FolderTree
                      folders={folderHierarchy}
                      currentPath={currentPath}
                      onFolderSelect={(folderPath) => fetchStoreFiles(folderPath)}
                    />
                  </div>
                  <select
                    onChange={(e) => handleStoreFileSelect(e.target.value)}
                    className="housekeeping-select"
                    value={selectedStoreFile || ''}
                    disabled={storeFiles.length === 0}
                    style={{ minWidth: '300px' }}
                  >
                    <option value="" disabled>Select a file from storage</option>
                    {storeFiles.map((file) => (
                      <option key={file} value={file}>
                        {file.split('/').pop()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {activeConnection === 'file' && selectedFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span
                    className="housekeeping-select hk-file-name-span"
                    style={{ 
                      cursor: 'pointer', 
                      display: 'inline-block', 
                      padding: '10px 12px', 
                      minWidth: '180px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </span>
                  {fileData.length > 0 && (
                    <span className="hk-records-badge">
                      {fileData.length} records loaded
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {activeConnection && showConnectedMessage && (
          <div className="housekeeping-connect-section">
            <span className="housekeeping-connected-message">
              {activeConnection === 'db' ? 'Connected' : activeConnection === 'drive' ? 'Drive Connected' : selectedFile?.name || 'Store Connected'}
            </span>
          </div>
        )}
        {errorMessage && activeConnection !== 'drive' && (
          <p className="housekeeping-error-message">{errorMessage}</p>
        )}

        {driveErrorMessage && (
          <p className="housekeeping-error-message">{driveErrorMessage}</p>
        )}

        {driveError && activeConnection === 'drive' && (
          <p className="housekeeping-error-message">{driveError}</p>
        )}

        {showReconnectButton && (
          <div className="hk-reconnect-banner">
            <span>Google Drive permissions need to be updated.</span>
            <button
              onClick={handleDriveReconnect}
              className="hk-reconnect-btn"
            >
              Reconnect Google Drive
            </button>
          </div>
        )}
        <div className="housekeeping-page-body">
          <div style={{ margin: '5px 0 0 0' }}>
            {activeConnection && (filteredFileData.length > 0 || filteredTableData.length > 0) && (
              <div className="housekeeping-control-section">
                <div className="control-left">
                  <div className="save-controls-group">
                    <div className="format-select-group">
                      <select
                        className="housekeeping-file-format-select"
                        value={fileFormat}
                        onChange={(e) => setFileFormat(e.target.value)}
                        style={{ minWidth: '140px', fontSize: '14px' }}
                      >
                        {isMediaFile ? (
                          <option value="bucket">Save to Bucket</option>
                        ) : (
                          <>
                            <option value="csv">CSV Format</option>
                            <option value="excel">Excel Format</option>
                            <option value="document">Firestore Document</option>
                            <option value="bucket">Save to Bucket</option>
                          </>
                        )}
                      </select>
                    </div>
                    <FaSave
                      className="housekeeping-icon-large"
                      onClick={handleSave}
                      title="Save"
                      style={{
                        color: (isMediaFile || selectedRecords.length > 0) && !isSaving ? undefined : 'var(--hk-icon-disabled)',
                        cursor: (isMediaFile || selectedRecords.length > 0) && !isSaving ? 'pointer' : 'not-allowed',
                        fontSize: '24px'
                      }}
                    />
                    {((activeConnection === 'db' && (fileFormat === 'csv' || fileFormat === 'document')) ||
                      (activeConnection === 'drive' && (fileFormat === 'csv' || fileFormat === 'document'))) ? (
                      <TbAutomation
                        className="housekeeping-icon-large"
                        onClick={handleAutomate}
                        title="Automate"
                        style={{
                          color: selectedColumns.length === 0 ? 'var(--hk-icon-disabled)' : undefined,
                          fontSize: '24px'
                        }}
                      />
                    ) : null}
                    {fileType !== 'video' && (
                      <FaPrint
                        className="housekeeping-icon-large"
                        onClick={handlePrint}
                        title="Print"
                        style={{
                          color: (isMediaFile || selectedRecords.length > 0) ? undefined : 'var(--hk-icon-disabled)',
                          fontSize: '24px'
                        }}
                      />
                    )}

                    {shouldShowDeleteButton() && (
                      <FaTrash
                        className="housekeeping-icon-large"
                        onClick={handleDelete}
                        title={getDeleteButtonTitle()}
                        style={{
                          fontSize: '24px',
                          cursor: 'pointer',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {isMediaFile && filteredFileData.length > 0 && (
              <div className="media-preview-section">
                <div className="media-preview-grid">
                  {filteredFileData.map((record, index) => (
                    <div key={index} className="media-preview-item">
                      {record.type !== 'video' && (
                        <div className="media-download-container">
                          <button
                            className="media-download-btn"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = record.preview;
                              link.download = record.name;
                              link.click();
                            }}
                            title="Download"
                          >
                            ⬇️
                          </button>
                        </div>
                      )}
                      {record.type === 'image' ? (
                        <img
                          src={record.preview}
                          alt={record.name}
                          className="media-preview-large"
                          onClick={() => openMediaPreview(record.preview, record.type, index)}
                        />
                      ) : record.type === 'video' ? (
                        <div className="video-preview-container">
                          <video
                            className="media-preview-large video-player"
                            controls
                            preload="metadata"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <source src={record.preview} type={`video/${record.name.split('.').pop()}`} />
                            Browser does not support the video tag.
                          </video>
                        </div>
                      ) : record.type === 'pdf' ? (
                        <div
                          className="media-preview-large"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff5f5',
                            cursor: 'pointer',
                            border: '2px solid #e74c3c',
                            borderRadius: '8px',
                            padding: '20px'
                          }}
                          onClick={() => openMediaPreview(record.preview, record.type, index)}
                        >
                          <div style={{ 
                            fontSize: '64px', 
                            marginBottom: '15px',
                            color: '#e74c3c'
                          }}>
                            📄
                          </div>
                          <div style={{
                            fontSize: '16px',
                            color: '#e74c3c',
                            fontWeight: 'bold',
                            textAlign: 'center'
                          }}>
                            PDF Document
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#dc3545',
                            marginTop: '10px',
                            padding: '4px 12px',
                            background: 'rgba(220, 53, 69, 0.1)',
                            borderRadius: '4px'
                          }}>
                            Click to view
                          </div>
                        </div>
                      ) : (
                        <div
                          className="media-preview-large"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer'
                          }}
                          onClick={() => openMediaPreview(record.preview, record.type, index)}
                        >
                          <div style={{ fontSize: '64px', marginBottom: '15px' }}>📄</div>
                        </div>
                      )}
                      <div className="media-info-stacked">
                        <div className="media-info-item">
                          <strong>Name:</strong>
                          <span>{record.name}</span>
                        </div>
                        <div className="media-info-item">
                          <strong>Type:</strong>
                          <span style={{ 
                            color: record.type === 'pdf' ? '#e74c3c' : 'inherit',
                            fontWeight: record.type === 'pdf' ? 'bold' : 'normal'
                          }}>
                            {record.type}
                          </span>
                        </div>
                        <div className="media-info-item">
                          <strong>Size:</strong>
                          <span>{record.size}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!isMediaFile && activeConnection && (
              <div className="housekeeping-tables-scroll-container">
                <div className="housekeeping-table-container">
                  <table className="housekeeping-table">
                    <thead>
                      <tr>
                        {!isMediaFile && (filteredFileData.length > 0 || filteredTableData.length > 0) && (
                          <th style={{ width: '60px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', paddingTop : '18px'}}>
                              {/* Column selection checkbox - only show for non-media files */}
                              {!isMediaFile && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <input
                                    type="checkbox"
                                    className="housekeeping-select-all-columns-checkbox"
                                    checked={selectAllColumns}
                                    onChange={handleSelectAllColumns}
                                    style={{ margin: '0' }}
                                  />
                                </div>
                              )}
                              {/* Row selection checkbox */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                <input
                                  type="checkbox"
                                  className="housekeeping-select-all-checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                  style={{ margin: '0' }}
                                />
                              </div>
                            </div>
                          </th>
                        )}
                        {headers.map((header, index) => (
                          <th key={index} className={header === 'preview' ? 'preview-column' : ''}>
                            <div className="housekeeping-th-content">
                              {editingHeaderIndex === index && header !== 'preview' ? (
                                <input
                                  type="text"
                                  value={editingHeaderValue}
                                  onChange={(e) => setEditingHeaderValue(e.target.value)}
                                  onBlur={() => {
                                    if (editingHeaderValue.trim() !== '' && editingHeaderValue !== header) {
                                      const oldHeaderName = header;
                                      const newHeaderName = editingHeaderValue.trim();
                                      
                                      // Update headers array
                                      const newHeaders = [...headers];
                                      newHeaders[index] = newHeaderName;
                                      setHeaders(newHeaders);
                                      
                                      // Update header mapping for tracking original names
                                      setHeaderMapping(prev => ({
                                        ...prev,
                                        [oldHeaderName]: newHeaderName
                                      }));
                                      
                                      // Update selectedColumns to replace old header with new header
                                      if (selectedColumns.includes(oldHeaderName)) {
                                        const updatedSelectedColumns = selectedColumns.map(col => 
                                          col === oldHeaderName ? newHeaderName : col
                                        );
                                        setSelectedColumns(updatedSelectedColumns);
                                      }
                                      
                                      // Update the data structure itself
                                      if (activeConnection === 'db') {
                                        // Update tableData
                                        const updatedTableData = tableData.map(row => {
                                          const newRow = { ...row };
                                          if (oldHeaderName in newRow) {
                                            newRow[newHeaderName] = newRow[oldHeaderName];
                                            delete newRow[oldHeaderName];
                                          }
                                          return newRow;
                                        });
                                        setTableData(updatedTableData);
                                        setFilteredTableData(updatedTableData);
                                        
                                        // Update filteredTableData
                                        const updatedFilteredTableData = filteredTableData.map(row => {
                                          const newRow = { ...row };
                                          if (oldHeaderName in newRow) {
                                            newRow[newHeaderName] = newRow[oldHeaderName];
                                            delete newRow[oldHeaderName];
                                          }
                                          return newRow;
                                        });
                                        setFilteredTableData(updatedFilteredTableData);
                                      } else {
                                        // Update fileData for file/drive/store connections
                                        const updatedFileData = fileData.map(row => {
                                          const newRow = { ...row };
                                          if (oldHeaderName in newRow) {
                                            newRow[newHeaderName] = newRow[oldHeaderName];
                                            delete newRow[oldHeaderName];
                                          }
                                          return newRow;
                                        });
                                        setFileData(updatedFileData);
                                        
                                        // Update filteredFileData
                                        const updatedFilteredFileData = filteredFileData.map(row => {
                                          const newRow = { ...row };
                                          if (oldHeaderName in newRow) {
                                            newRow[newHeaderName] = newRow[oldHeaderName];
                                            delete newRow[oldHeaderName];
                                          }
                                          return newRow;
                                        });
                                        setFilteredFileData(updatedFilteredFileData);
                                      }
                                    }
                                    setEditingHeaderIndex(null);
                                    setEditingHeaderValue('');
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      if (editingHeaderValue.trim() !== '' && editingHeaderValue !== header) {
                                        const oldHeaderName = header;
                                        const newHeaderName = editingHeaderValue.trim();
                                        
                                        // Update headers array
                                        const newHeaders = [...headers];
                                        newHeaders[index] = newHeaderName;
                                        setHeaders(newHeaders);
                                        
                                        // Update header mapping for tracking original names
                                        setHeaderMapping(prev => ({
                                          ...prev,
                                          [oldHeaderName]: newHeaderName
                                        }));
                                        
                                        // Update selectedColumns to replace old header with new header
                                        if (selectedColumns.includes(oldHeaderName)) {
                                          const updatedSelectedColumns = selectedColumns.map(col => 
                                            col === oldHeaderName ? newHeaderName : col
                                          );
                                          setSelectedColumns(updatedSelectedColumns);
                                        }
                                        
                                        // Update the data structure itself
                                        if (activeConnection === 'db') {
                                          // Update tableData
                                          const updatedTableData = tableData.map(row => {
                                            const newRow = { ...row };
                                            if (oldHeaderName in newRow) {
                                              newRow[newHeaderName] = newRow[oldHeaderName];
                                              delete newRow[oldHeaderName];
                                            }
                                            return newRow;
                                          });
                                          setTableData(updatedTableData);
                                          
                                          // Update filteredTableData
                                          const updatedFilteredTableData = filteredTableData.map(row => {
                                            const newRow = { ...row };
                                            if (oldHeaderName in newRow) {
                                              newRow[newHeaderName] = newRow[oldHeaderName];
                                              delete newRow[oldHeaderName];
                                            }
                                            return newRow;
                                          });
                                          setFilteredTableData(updatedFilteredTableData);
                                        } else {
                                          // Update fileData for file/drive/store connections
                                          const updatedFileData = fileData.map(row => {
                                            const newRow = { ...row };
                                            if (oldHeaderName in newRow) {
                                              newRow[newHeaderName] = newRow[oldHeaderName];
                                              delete newRow[oldHeaderName];
                                            }
                                            return newRow;
                                          });
                                          setFileData(updatedFileData);
                                          
                                          // Update filteredFileData
                                          const updatedFilteredFileData = filteredFileData.map(row => {
                                            const newRow = { ...row };
                                            if (oldHeaderName in newRow) {
                                              newRow[newHeaderName] = newRow[oldHeaderName];
                                              delete newRow[oldHeaderName];
                                            }
                                            return newRow;
                                          });
                                          setFilteredFileData(updatedFilteredFileData);
                                        }
                                      }
                                      setEditingHeaderIndex(null);
                                      setEditingHeaderValue('');
                                    } else if (e.key === 'Escape') {
                                      setEditingHeaderIndex(null);
                                      setEditingHeaderValue('');
                                    }
                                  }}
                                  autoFocus
                                  className="housekeeping-header-edit-input"
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #2196f3',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    width: '100%'
                                  }}
                                />
                              ) : (
                                <span 
                                  className="housekeeping-header-text"
                                  onClick={() => {
                                    if (header !== 'preview' && !isMediaFile) {
                                      setEditingHeaderIndex(index);
                                      setEditingHeaderValue(header);
                                    }
                                  }}
                                  style={{
                                    cursor: header !== 'preview' && !isMediaFile ? 'pointer' : 'default',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-block',
                                    minWidth: '100px'
                                  }}
                                >
                                  {header}
                                </span>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <FaSort
                                  className="housekeeping-sort-icon"
                                  onClick={() => {
                                    if (editingHeaderIndex === null) {
                                      handleSort(header);
                                    }
                                  }}
                                  style={{
                                    display: sortConfig.key !== header ? 'inline-block' : 'none',
                                    cursor: editingHeaderIndex === null ? 'pointer' : 'not-allowed',
                                    opacity: editingHeaderIndex === null ? 1 : 0.5
                                  }} />
                                <FaSortUp
                                  className="housekeeping-sort-icon"
                                  onClick={() => {
                                    if (editingHeaderIndex === null) {
                                      handleSort(header);
                                    }
                                  }}
                                  style={{
                                    display: sortConfig.key === header && sortConfig.direction === 'asc' ? 'inline-block' : 'none',
                                    cursor: editingHeaderIndex === null ? 'pointer' : 'not-allowed',
                                    opacity: editingHeaderIndex === null ? 1 : 0.5
                                  }} />
                                <FaSortDown
                                  className="housekeeping-sort-icon"
                                  onClick={() => {
                                    if (editingHeaderIndex === null) {
                                      handleSort(header);
                                    }
                                  }}
                                  style={{
                                    display: sortConfig.key === header && sortConfig.direction === 'desc' ? 'inline-block' : 'none',
                                    cursor: editingHeaderIndex === null ? 'pointer' : 'not-allowed',
                                    opacity: editingHeaderIndex === null ? 1 : 0.5
                                  }} />
                                {!isMediaFile && editingHeaderIndex === null && (
                                  <input
                                    type="checkbox"
                                    className="housekeeping-column-checkbox"
                                    checked={selectedColumns.includes(header)}
                                    onChange={() => handleColumnSelect(header)}
                                    style={{
                                      cursor: editingHeaderIndex === null ? 'pointer' : 'not-allowed',
                                      opacity: editingHeaderIndex === null ? 1 : 0.5
                                    }} />
                                )}
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {((activeConnection === 'db' && filteredTableData.length > 0) || ((activeConnection === 'file' || activeConnection === 'drive' || activeConnection === 'store') && filteredFileData.length > 0)) ? (
                        (activeConnection === 'db' ? filteredTableData : filteredFileData).map((record, index) => (
                          <tr key={index}>
                            {!isMediaFile && (
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  className="housekeeping-row-checkbox"
                                  checked={selectedRecords.includes(index)}
                                  onChange={() => handleSelectRecord(index)} />
                              </td>
                            )}
                            {headers.map((header, i) => {
                              if (header === 'preview' && isMediaFile) {
                                return (
                                  <td key={i}>
                                    <MediaPreview
                                      record={record}
                                      index={index}
                                      onPreviewClick={openMediaPreview}
                                    />
                                  </td>
                                );
                              }
                             
                              const value = record[header];
                              const displayValue = value !== undefined && value !== null ? String(value) : '';
                              return (
                                <td key={i} title={displayValue}>
                                  {displayValue}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={headers.length + 1} style={{ textAlign: 'center' }}>
                            <p className="housekeeping-placeholder-message">
                              {searchQuery ?
                                'No matching records found' :
                                'No data selected. Please select a table or file.'}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <DynamicFormModal
          isOpen={!!activeModalConfig}
          onRequestClose={() => setActiveModalConfig(null)}
          config={activeModalConfig || { title: '', fields: [], onSubmit: async () => {} }}
        />
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onRequestClose={() => {
            setIsDeleteModalOpen(false);
            setDeleteTarget(null);
            setDeleteContext(null);
          }}
          onConfirm={confirmDelete}
          onConfirmMulti={confirmMultiDelete}
          deleteTarget={deleteTarget}
          itemType="file"
          requirePassword={true}
          submitting={deleting}
          context={deleteContext}
          showMultiDeleteOption={activeConnection === 'store' || (activeConnection === 'db' && selectedTable)}
        />
        <MediaPreviewModal
          isOpen={mediaPreviewOpen}
          onRequestClose={() => setMediaPreviewOpen(false)}
          mediaFiles={mediaFiles}
          currentIndex={currentMediaIndex}
          onNavigate={navigateMedia}
        />
      </div>
      </div>
  </>
  );
};

export default HouseKeeping;